import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API_BASE, euro, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PaymentBadge } from "@/components/PaymentBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Ear, FileText, Plus, Trash2, Printer, Pencil, FileCheck2, Phone, Stethoscope, Download, RefreshCw, Undo2, ImagePlus, FileDown, MessageCircle } from "lucide-react";
import { printTicketBlob } from "@/lib/ticketPrinter";
import { toast } from "sonner";
import { SPANISH_PROVINCES, LEAD_SOURCE_OPTIONS, CONTACT_CHANNELS, CONTACT_RESULTS } from "@/lib/constants";

export default function PatientDetail() {
  const { id } = useParams();
  const { user, centers } = useAuth();
  const [patient, setPatient] = useState(null);
  const [anamnesisList, setAnamnesisList] = useState([]);
  const [hearingAids, setHearingAids] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [visits, setVisits] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [trials, setTrials] = useState([]);

  const [audiograms, setAudiograms] = useState([]);
  const [clinicalTests, setClinicalTests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [audiologists, setAudiologists] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setAudiologists(r.data.filter((u) => ["audioprotesista", "admin", "gerente"].includes(u.role) && u.active !== false))).catch(() => {});
  }, []);

  const load = async () => {
    const [p, a, h, inv, qts, vs, rs, ts, ags, cts, cts2] = await Promise.all([
      api.get(`/patients/${id}`).then((r) => r.data),
      api.get(`/patients/${id}/anamnesis`).then((r) => r.data),
      api.get(`/patients/${id}/hearing-aids`).then((r) => r.data),
      api.get("/invoices", { params: { patient_id: id } }).then((r) => r.data),
      api.get("/quotes", { params: { patient_id: id } }).then((r) => r.data),
      api.get(`/patients/${id}/visits`).then((r) => r.data),
      api.get(`/patients/${id}/repairs`).then((r) => r.data),
      api.get(`/patients/${id}/trials`).then((r) => r.data),
      api.get(`/patients/${id}/audiograms`).then((r) => r.data).catch(() => []),
      api.get(`/patients/${id}/contacts`).then((r) => r.data).catch(() => []),
      api.get(`/patients/${id}/clinical-tests`).then((r) => r.data).catch(() => []),
    ]);
    setPatient(p);
    setAnamnesisList(a);
    setHearingAids(h);
    setInvoices(inv);
    setQuotes(qts);
    setVisits(vs);
    setRepairs(rs);
    setTrials(ts);
    setAudiograms(ags);
    setContacts(cts);
    setClinicalTests(cts2);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line
  }, [id]);

  if (!patient) return <div className="text-[#6E7772]">Cargando…</div>;

  const centerName = centers.find((c) => c.id === patient.center_id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="patient-detail">
      <Link
        to="/pacientes"
        className="inline-flex items-center text-sm text-[#6E7772] hover:text-[#2A2F2C]"
      >
        <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Volver a pacientes
      </Link>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#E5E3DB] pb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Paciente #{String(patient.patient_number).padStart(4, "0")}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1 flex items-center gap-3">
            {patient.name} {patient.surname}
            {patient.status === "deceased" && (
              <Badge variant="outline" className="text-xs border-[#9CA3AF] text-[#6E7772]" data-testid="patient-deceased-badge">
                † Fallecido {patient.deceased_date || ""}
              </Badge>
            )}
            {patient.status === "inactive" && (
              <Badge variant="outline" className="text-xs">Inactivo</Badge>
            )}
          </h1>
          <div className="text-sm text-[#6E7772] mt-1">
            {centerName} · Alta {formatDate(patient.created_at)}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-sm text-[#4B5563] items-center">
          {patient.dni && <Badge variant="outline" className="border-[#E5E3DB]">DNI {patient.dni}</Badge>}
          {patient.phone && <Badge variant="outline" className="border-[#E5E3DB]">{patient.phone}</Badge>}
          {patient.phone_alt && (
            <Badge variant="outline" className="border-[#E5E3DB]" title={patient.phone_alt_relation || "Contacto adicional"}>
              {patient.phone_alt_relation ? `${patient.phone_alt_relation}: ` : ""}{patient.phone_alt}
            </Badge>
          )}
          {patient.email && <Badge variant="outline" className="border-[#E5E3DB]">{patient.email}</Badge>}
          {patient.disability && (
            <Badge className="bg-[#D1E0D7] text-[#2A2F2C]" data-testid="patient-disability-badge">
              ♿ Minusvalía{patient.disability_percent ? ` ${patient.disability_percent}%` : ""} · IVA 4%
            </Badge>
          )}
          <PatientStatusButton patient={patient} onChange={load} />
        </div>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="bg-[#EAE6DF] flex-wrap h-auto">
          <TabsTrigger value="info" data-testid="tab-info">Datos</TabsTrigger>
          <TabsTrigger value="anamnesis" data-testid="tab-anamnesis">Anamnesis</TabsTrigger>
          <TabsTrigger value="hearing-aids" data-testid="tab-hearing-aids">
            Dispositivos vinculados {trials.length > 0 && <span className="ml-1 text-[10px] bg-[#C07F67] text-white rounded-full px-1.5">trial</span>}
          </TabsTrigger>
          <TabsTrigger value="clinical" data-testid="tab-clinical">
            Pruebas clínicas {clinicalTests.length > 0 && <span className="ml-1 text-[10px] bg-[#4A5D4E] text-white rounded-full px-1.5">{clinicalTests.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            Facturas/Presupuestos
          </TabsTrigger>
          <TabsTrigger value="repairs" data-testid="tab-repairs">Reparaciones</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            Contactos {contacts.length > 0 && <span className="ml-1 text-[10px] bg-[#6E7772] text-white rounded-full px-1.5">{contacts.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <PatientInfoEditor patient={patient} centers={centers} onSaved={load} />
        </TabsContent>

        <TabsContent value="anamnesis" className="mt-6 space-y-4">
          <AnamnesisSection
            patientId={id}
            anamnesisList={anamnesisList}
            onCreated={load}
          />
        </TabsContent>

        <TabsContent value="hearing-aids" className="mt-6 space-y-4">
          <HearingAidsSection
            patientId={id}
            hearingAids={hearingAids}
            trials={trials}
            onChange={load}
          />
          <AccessoriesSection patientId={id} user={user} />
        </TabsContent>

        <TabsContent value="clinical" className="mt-6 space-y-4">
          <ClinicalTestsSection
            patient={patient}
            tests={clinicalTests}
            audiologists={audiologists}
            user={user}
            onChange={load}
          />
        </TabsContent>

        <TabsContent value="invoices" className="mt-6 space-y-4">
          <InvoicesSection
            patient={patient}
            invoices={invoices}
            quotes={quotes}
            hearingAids={hearingAids}
            onChange={load}
            user={user}
          />
        </TabsContent>

        <TabsContent value="repairs" className="mt-6 space-y-4">
          <RepairsSection
            patient={patient}
            repairs={repairs}
            hearingAids={hearingAids}
            onChange={load}
          />
        </TabsContent>

        <TabsContent value="contacts" className="mt-6 space-y-4">
          <ContactsSection
            patientId={id}
            patient={patient}
            contacts={contacts}
            onChange={load}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-6 space-y-4">
          <HistorySection patientId={id} visits={visits} onChange={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
        {label}
      </div>
      <div className="text-[#2A2F2C] mt-1">{value}</div>
    </div>
  );
}

function PatientStatusButton({ patient, onChange }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const isDeceased = patient.status === "deceased";

  const reactivate = async () => {
    if (!window.confirm("¿Reactivar al paciente?")) return;
    try {
      await api.patch(`/patients/${patient.id}/status`, { status: "active" });
      toast.success("Paciente reactivado");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitDeceased = async () => {
    setSaving(true);
    try {
      await api.patch(`/patients/${patient.id}/status`, {
        status: "deceased",
        deceased_date: date,
        deceased_notes: notes,
      });
      toast.success("Paciente marcado como fallecido");
      setOpen(false);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (isDeceased) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={reactivate}
        data-testid="reactivate-patient-btn"
        className="text-[#4A5D4E]"
      >
        Reactivar
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid="mark-deceased-btn"
          className="text-[#6E7772]"
        >
          † Dar de baja por defunción
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Baja por defunción</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#6E7772]">
            El paciente se marcará como fallecido y dejará de aparecer en listados activos
            y en notificaciones, pero su historial completo se conservará.
          </p>
          <div>
            <Label>Fecha de fallecimiento</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="deceased-date" />
          </div>
          <div>
            <Label>Notas (opcional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Información adicional" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            onClick={submitDeceased}
            disabled={saving}
            data-testid="confirm-deceased-btn"
            className="bg-[#6E7772] hover:bg-[#4B5563] text-white"
          >
            {saving ? "Guardando…" : "Confirmar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PatientInfoEditor({ patient, centers, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [audiologists, setAudiologists] = useState([]);
  const buildForm = (p) => ({
    name: p.name || "",
    surname: p.surname || "",
    dni: p.dni || "",
    dob: p.dob || "",
    gender: p.gender || "",
    phone: p.phone || "",
    phone_alt: p.phone_alt || "",
    phone_alt_relation: p.phone_alt_relation || "",
    email: p.email || "",
    address: p.address || "",
    city: p.city || "",
    postal_code: p.postal_code || "",
    province: p.province || "",
    center_id: p.center_id || "",
    lead_source: p.lead_source || "",
    referring_doctor: p.referring_doctor || "",
    notes: p.notes || "",
    audiologist_ids: p.audiologist_ids || [],
    primary_audiologist_id: p.primary_audiologist_id || "",
    disability: !!p.disability,
    disability_percent: p.disability_percent || "",
  });
  const [form, setForm] = useState(buildForm(patient));

  useEffect(() => {
    setForm(buildForm(patient));
  }, [patient]);

  useEffect(() => {
    api.get("/users").then((r) => setAudiologists(r.data.filter((u) => ["audioprotesista", "admin", "gerente"].includes(u.role) && u.active !== false))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        disability_percent: form.disability && form.disability_percent
          ? parseInt(form.disability_percent) || null
          : null,
      };
      await api.patch(`/patients/${patient.id}`, payload);
      toast.success("Ficha actualizada");
      setEditing(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const centerName = centers.find((c) => c.id === patient.center_id)?.name || "—";
  const audName = (id) => audiologists.find((a) => a.id === id)?.name || id;

  if (!editing) {
    return (
      <Card className="bg-white border-[#E5E3DB] shadow-none">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4 text-sm">
            <Info label="Nombre completo" value={`${patient.name} ${patient.surname}`} />
            <Info label="DNI / NIE" value={patient.dni || "—"} />
            <Info label="Fecha nacimiento" value={patient.dob || "—"} />
            <Info label="Género" value={patient.gender || "—"} />
            <Info label="Teléfono" value={patient.phone || "—"} />
            <Info label="Tel. adicional" value={patient.phone_alt ? `${patient.phone_alt}${patient.phone_alt_relation ? ` (${patient.phone_alt_relation})` : ""}` : "—"} />
            <Info label="Email" value={patient.email || "—"} />
            <Info label="Dirección" value={patient.address || "—"} />
            <Info label="Población" value={patient.city || "—"} />
            <Info label="Código postal" value={patient.postal_code || "—"} />
            <Info label="Provincia" value={patient.province || "—"} />
            <Info label="Centro" value={centerName} />
            <Info label="Procedencia" value={patient.lead_source || "—"} />
            <Info label="Médico derivante" value={patient.referring_doctor || "—"} />
            <Info label="Audioprotesistas" value={
              (patient.audiologist_ids && patient.audiologist_ids.length)
                ? patient.audiologist_ids.map((id) => audName(id) + (id === patient.primary_audiologist_id ? " (principal)" : "")).join(", ")
                : "—"
            } />
            {patient.notes && (
              <div className="md:col-span-2 xl:col-span-3">
                <Info label="Notas" value={patient.notes} />
              </div>
            )}
          </div>
          <div className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-patient-btn"
              className="border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#EAE6DF]"
            >
              <Pencil className="h-4 w-4 mr-1" /> Editar ficha
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E5E3DB] shadow-none">
      <CardContent className="p-6">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                data-testid="edit-patient-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Apellidos</Label>
              <Input
                data-testid="edit-patient-surname"
                value={form.surname}
                onChange={(e) => setForm({ ...form, surname: e.target.value })}
              />
            </div>
            <div>
              <Label>DNI / NIE</Label>
              <Input
                data-testid="edit-patient-dni"
                value={form.dni}
                onChange={(e) => setForm({ ...form, dni: e.target.value })}
              />
            </div>
            <div>
              <Label>Fecha nacimiento</Label>
              <Input
                type="date"
                data-testid="edit-patient-dob"
                value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
              />
            </div>
            <div>
              <Label>Género</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="edit-patient-gender" className="bg-white">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hombre">Hombre</SelectItem>
                  <SelectItem value="mujer">Mujer</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                data-testid="edit-patient-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Tel. adicional</Label>
              <Input
                data-testid="edit-patient-phone-alt"
                value={form.phone_alt}
                onChange={(e) => setForm({ ...form, phone_alt: e.target.value })}
                placeholder="Familiar / amigo"
              />
            </div>
            <div>
              <Label>Relación del contacto</Label>
              <Input
                data-testid="edit-patient-phone-alt-rel"
                value={form.phone_alt_relation}
                onChange={(e) => setForm({ ...form, phone_alt_relation: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                data-testid="edit-patient-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Centro</Label>
              <Select
                value={form.center_id}
                onValueChange={(v) => setForm({ ...form, center_id: v })}
              >
                <SelectTrigger data-testid="edit-patient-center" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Dirección</Label>
              <Input
                data-testid="edit-patient-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div>
              <Label>Población</Label>
              <Input
                data-testid="edit-patient-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div>
              <Label>Código postal</Label>
              <Input
                data-testid="edit-patient-postal-code"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                placeholder="12005"
                inputMode="numeric"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Provincia</Label>
              <Select
                value={form.province || "none"}
                onValueChange={(v) => setForm({ ...form, province: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-patient-province" className="bg-white">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">—</SelectItem>
                  {SPANISH_PROVINCES.map((prov) => (
                    <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Médico derivante (opcional)</Label>
              <Input
                data-testid="edit-patient-referring-doctor"
                value={form.referring_doctor}
                onChange={(e) => setForm({ ...form, referring_doctor: e.target.value })}
              />
            </div>
            <div>
              <Label>Procedencia</Label>
              <Select value={form.lead_source} onValueChange={(v) => setForm({ ...form, lead_source: v })}>
                <SelectTrigger data-testid="edit-patient-source" className="bg-white">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rrss">Redes sociales</SelectItem>
                  <SelectItem value="flyers">Flyers</SelectItem>
                  <SelectItem value="web">Web</SelectItem>
                  <SelectItem value="boca_a_boca">Boca a boca</SelectItem>
                  <SelectItem value="calle">Calle</SelectItem>
                  <SelectItem value="tram">Tranvía</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <Label>Audioprotesistas asignados</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-2 border rounded-md bg-[#FAF9F6]" data-testid="edit-patient-audiologists">
                {audiologists.length === 0 && (
                  <span className="text-xs text-[#6E7772]">Sin audioprotesistas en el sistema.</span>
                )}
                {audiologists.map((au) => {
                  const checked = form.audiologist_ids.includes(au.id);
                  const isPrimary = form.primary_audiologist_id === au.id;
                  return (
                    <label
                      key={au.id}
                      className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                        checked ? "bg-[#4A5D4E] text-white" : "bg-white border border-[#E5E3DB] text-[#4B5563]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        className="hidden"
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...form.audiologist_ids, au.id]
                            : form.audiologist_ids.filter((x) => x !== au.id);
                          setForm({
                            ...form,
                            audiologist_ids: next,
                            primary_audiologist_id: next.includes(form.primary_audiologist_id)
                              ? form.primary_audiologist_id
                              : (next[0] || ""),
                          });
                        }}
                      />
                      {au.name}
                      {au.role !== "audioprotesista" && (
                        <span
                          className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            checked ? "bg-white/20 text-white" : "bg-[#EAE6DF] text-[#6E7772]"
                          }`}
                          title={`Este usuario es ${au.role === "admin" ? "administrador" : "gerente"} y también ejerce como audioprotesista`}
                        >
                          {au.role === "admin" ? "Admin" : "Gerente"}
                        </span>
                      )}
                      {checked && (
                        <button
                          type="button"
                          className={`ml-1 text-[10px] px-1 rounded ${isPrimary ? "bg-white text-[#4A5D4E]" : "bg-[#D9D6CC] text-[#2A2F2C]"}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setForm({ ...form, primary_audiologist_id: au.id });
                          }}
                          data-testid={`edit-set-primary-${au.id}`}
                        >
                          {isPrimary ? "Principal" : "Hacer principal"}
                        </button>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2 xl:col-span-3 mt-2 pt-3 border-t border-[#E5E3DB]">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="edit-patient-disability"
                  checked={!!form.disability}
                  onChange={(e) => setForm({ ...form, disability: e.target.checked })}
                />
                <span className="font-medium">Tiene certificado de minusvalía</span>
                <span className="text-xs text-[#6E7772]">(IVA 4% en facturas)</span>
              </label>
              {form.disability && (
                <div className="mt-2 max-w-xs">
                  <Label className="text-xs">% de minusvalía (opcional)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={form.disability_percent}
                    onChange={(e) => setForm({ ...form, disability_percent: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2 xl:col-span-3">
              <Label>Notas</Label>
              <Textarea
                data-testid="edit-patient-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-patient-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AnamnesisSection({ patientId, anamnesisList, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    profession: "",
    lead_source: "",
    uses_hearing_aid: false,
    current_hearing_aid: "",
    time_as_user: "",
    satisfaction: "",
    adaptation_place: "",
    doctor_visited: "",
    pain_dizziness_discharge: "",
    tinnitus: false,
    vertigo: false,
    ear_surgery: false,
    family_history_yes: false,
    family_history: "",
    wax_plug_removed: false,
    wax_plug_left: false,
    wax_plug_right: false,
    wax_plug_notes: "",
    hearing_problems: [],
    medications: "",
    work_environment: "",
    otoscopy_result: "",
    has_hearing_loss: false,
    hearing_loss_side: "",
    complaints: "",
    hearing_history: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const toggleProblem = (key) => {
    setForm((f) => ({
      ...f,
      hearing_problems: f.hearing_problems.includes(key)
        ? f.hearing_problems.filter((p) => p !== key)
        : [...f.hearing_problems, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/anamnesis`, form);
      toast.success("Anamnesis registrada");
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#6E7772]">
          Registro de antecedentes y exploración auditiva.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-anamnesis-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Nueva anamnesis
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nueva anamnesis</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5 max-h-[72vh] overflow-y-auto pr-2 scrollbar-thin">
              {/* Sección 1: Datos contextuales */}
              <div>
                <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
                  Datos del paciente
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Profesión</Label>
                    <Input
                      data-testid="anamnesis-profession"
                      value={form.profession}
                      onChange={(e) => setForm({ ...form, profession: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>¿Cómo nos conoció? (Procedencia)</Label>
                    <Select
                      value={form.lead_source || "none"}
                      onValueChange={(v) => setForm({ ...form, lead_source: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="anamnesis-lead-source"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="tram">Tranvía / metro</SelectItem>
                        <SelectItem value="flyers">Flyers</SelectItem>
                        <SelectItem value="rrss">Redes sociales</SelectItem>
                        <SelectItem value="boca_a_boca">Boca a boca</SelectItem>
                        <SelectItem value="calle">Calle</SelectItem>
                        <SelectItem value="web">Web</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Sección 2: Audífono actual */}
              <div>
                <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
                  Uso actual de audífono
                </div>
                <label className="flex items-center gap-2 text-sm mb-2">
                  <Checkbox
                    checked={form.uses_hearing_aid}
                    onCheckedChange={(v) => setForm({ ...form, uses_hearing_aid: !!v })}
                    data-testid="anamnesis-uses-aid"
                  />
                  ¿Usa actualmente audífono?
                </label>
                {form.uses_hearing_aid && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6 border-l-2 border-[#EAE6DF]">
                    <div>
                      <Label>Marca y modelo</Label>
                      <Input
                        value={form.current_hearing_aid}
                        onChange={(e) => setForm({ ...form, current_hearing_aid: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Tiempo como usuario</Label>
                      <Input
                        placeholder="ej. 2 años"
                        value={form.time_as_user}
                        onChange={(e) => setForm({ ...form, time_as_user: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Satisfacción</Label>
                      <Select value={form.satisfaction || "none"} onValueChange={(v) => setForm({ ...form, satisfaction: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          <SelectItem value="bajo">Bajo</SelectItem>
                          <SelectItem value="medio">Medio</SelectItem>
                          <SelectItem value="alto">Alto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>¿Dónde le hicieron la adaptación?</Label>
                      <Input
                        value={form.adaptation_place}
                        onChange={(e) => setForm({ ...form, adaptation_place: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Sección 3: Historia clínica */}
              <div>
                <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
                  Historia clínica
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>¿Ha visitado el doctor especialista?</Label>
                    <Input
                      value={form.doctor_visited}
                      onChange={(e) => setForm({ ...form, doctor_visited: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Dolores, mareos, supuraciones</Label>
                    <Input
                      value={form.pain_dizziness_discharge}
                      onChange={(e) => setForm({ ...form, pain_dizziness_discharge: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {[
                    { key: "tinnitus", label: "Acúfenos" },
                    { key: "vertigo", label: "Vértigo / mareos" },
                    { key: "ear_surgery", label: "Cirugía previa" },
                  ].map((c) => (
                    <label key={c.key} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={form[c.key]}
                        onCheckedChange={(v) => setForm({ ...form, [c.key]: !!v })}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
                <div className="mt-3">
                  <label className="flex items-center gap-2 text-sm mb-1">
                    <Checkbox
                      checked={form.family_history_yes}
                      onCheckedChange={(v) => setForm({ ...form, family_history_yes: !!v })}
                    />
                    Antecedentes familiares
                  </label>
                  {form.family_history_yes && (
                    <Input
                      placeholder="Detalles (ej. madre, sordera neurosensorial)"
                      value={form.family_history}
                      onChange={(e) => setForm({ ...form, family_history: e.target.value })}
                    />
                  )}
                </div>
                {/* Tapón de cera */}
                <div className="mt-4 pt-3 border-t border-[#E5E3DB]">
                  <label className="flex items-center gap-2 text-sm font-medium mb-2">
                    <Checkbox
                      data-testid="anamnesis-wax-plug"
                      checked={form.wax_plug_removed}
                      onCheckedChange={(v) => setForm({
                        ...form,
                        wax_plug_removed: !!v,
                        ...((!v) && { wax_plug_left: false, wax_plug_right: false, wax_plug_notes: "" }),
                      })}
                    />
                    ¿Le han quitado tapón de cera alguna vez?
                  </label>
                  {form.wax_plug_removed && (
                    <div className="ml-6 space-y-2">
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            data-testid="anamnesis-wax-plug-left"
                            checked={form.wax_plug_left}
                            onCheckedChange={(v) => setForm({ ...form, wax_plug_left: !!v })}
                          />
                          <span className="text-[#3B6BB0] font-medium">Oído izquierdo (OI)</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            data-testid="anamnesis-wax-plug-right"
                            checked={form.wax_plug_right}
                            onCheckedChange={(v) => setForm({ ...form, wax_plug_right: !!v })}
                          />
                          <span className="text-[#C04848] font-medium">Oído derecho (OD)</span>
                        </label>
                      </div>
                      <div>
                        <Label className="text-xs">Observaciones (frecuencia, último episodio, profesional…)</Label>
                        <Textarea
                          data-testid="anamnesis-wax-plug-notes"
                          rows={2}
                          value={form.wax_plug_notes}
                          onChange={(e) => setForm({ ...form, wax_plug_notes: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sección 4: Problemas cotidianos */}
              <div>
                <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
                  ¿Qué problema cotidiano le plantea la falta de audición?
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: "conversaciones", label: "Conversaciones" },
                    { key: "tv", label: "T.V." },
                    { key: "ambientes_ruidosos", label: "Ambientes ruidosos" },
                  ].map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={form.hearing_problems.includes(p.key)}
                        onCheckedChange={() => toggleProblem(p.key)}
                        data-testid={`anamnesis-problem-${p.key}`}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Sección 5: Exploración */}
              <div>
                <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
                  Exploración
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Medicación actual</Label>
                    <Input
                      value={form.medications}
                      onChange={(e) => setForm({ ...form, medications: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Entorno laboral / ruido</Label>
                    <Input
                      value={form.work_environment}
                      onChange={(e) => setForm({ ...form, work_environment: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Resultado otoscopia</Label>
                  <Textarea
                    rows={2}
                    value={form.otoscopy_result}
                    onChange={(e) => setForm({ ...form, otoscopy_result: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.has_hearing_loss}
                      onCheckedChange={(v) => setForm({ ...form, has_hearing_loss: !!v })}
                      data-testid="anamnesis-has-loss"
                    />
                    Pérdida auditiva detectada
                  </label>
                  {form.has_hearing_loss && (
                    <div>
                      <Select
                        value={form.hearing_loss_side || "none"}
                        onValueChange={(v) => setForm({ ...form, hearing_loss_side: v === "none" ? "" : v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Lado afectado" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          <SelectItem value="izquierdo">Izquierdo</SelectItem>
                          <SelectItem value="derecho">Derecho</SelectItem>
                          <SelectItem value="ambos">Ambos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Sección 6: Texto libre */}
              <div>
                <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
                  Observaciones
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Motivo de consulta</Label>
                    <Textarea
                      data-testid="anamnesis-complaints"
                      rows={2}
                      value={form.complaints}
                      onChange={(e) => setForm({ ...form, complaints: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Antecedentes auditivos</Label>
                    <Textarea
                      rows={2}
                      value={form.hearing_history}
                      onChange={(e) => setForm({ ...form, hearing_history: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Notas adicionales</Label>
                    <Textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="sticky bottom-0 bg-white pt-3 -mx-2 px-2 border-t border-[#E5E3DB]">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-anamnesis-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar anamnesis"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {anamnesisList.length === 0 ? (
        <Card className="border-[#E5E3DB] shadow-none bg-white">
          <CardContent className="p-12 text-center text-[#6E7772]">
            Sin registros de anamnesis.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {anamnesisList.map((a) => (
            <Card key={a.id} className="border-[#E5E3DB] shadow-none bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading font-medium text-base flex items-center justify-between flex-wrap gap-2">
                  <span>{formatDate(a.performed_at)}</span>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {a.has_hearing_loss && (
                      <Badge className="bg-[#E8D1D1] text-[#2A2F2C]">Pérdida {a.hearing_loss_side || ""}</Badge>
                    )}
                    {!a.has_hearing_loss && a.profession && (
                      <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">Sin pérdida</Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                  {a.profession && <div><strong>Profesión:</strong> {a.profession}</div>}
                  {a.lead_source && <div><strong>Procedencia:</strong> {a.lead_source}</div>}
                  {a.uses_hearing_aid && (
                    <div><strong>Audífono actual:</strong> {a.current_hearing_aid || "—"}</div>
                  )}
                  {a.time_as_user && <div><strong>Tiempo de uso:</strong> {a.time_as_user}</div>}
                  {a.satisfaction && <div><strong>Satisfacción:</strong> {a.satisfaction}</div>}
                  {a.adaptation_place && <div><strong>Adaptación previa:</strong> {a.adaptation_place}</div>}
                  {a.doctor_visited && <div><strong>Especialista:</strong> {a.doctor_visited}</div>}
                  {a.pain_dizziness_discharge && (
                    <div><strong>Dolores/mareos/supuración:</strong> {a.pain_dizziness_discharge}</div>
                  )}
                  {a.medications && <div><strong>Medicación:</strong> {a.medications}</div>}
                  {a.otoscopy_result && (
                    <div className="md:col-span-2"><strong>Otoscopia:</strong> {a.otoscopy_result}</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {a.tinnitus && <Badge className="bg-[#F3E5D8] text-[#2A2F2C]">Acúfenos</Badge>}
                  {a.vertigo && <Badge className="bg-[#F3E5D8] text-[#2A2F2C]">Vértigo</Badge>}
                  {a.ear_surgery && <Badge className="bg-[#F3E5D8] text-[#2A2F2C]">Cirugía previa</Badge>}
                  {a.family_history_yes && (
                    <Badge className="bg-[#EAE6DF] text-[#2A2F2C]">
                      Antecedentes fam.{a.family_history ? `: ${a.family_history}` : ""}
                    </Badge>
                  )}
                  {a.wax_plug_removed && (
                    <Badge className="bg-[#F1E5C0] text-[#2A2F2C]" data-testid="anamnesis-wax-plug-badge">
                      Tapón cera extraído
                      {a.wax_plug_left && a.wax_plug_right ? " (ambos)"
                        : a.wax_plug_left ? " (OI)"
                        : a.wax_plug_right ? " (OD)"
                        : ""}
                    </Badge>
                  )}
                  {a.hearing_problems?.length > 0 &&
                    a.hearing_problems.map((p) => (
                      <Badge key={p} className="bg-[#D1E0D7] text-[#2A2F2C]">{p}</Badge>
                    ))}
                </div>
                {a.wax_plug_removed && a.wax_plug_notes && (
                  <div><strong>Tapón de cera:</strong> {a.wax_plug_notes}</div>
                )}
                {a.complaints && <div><strong>Motivo:</strong> {a.complaints}</div>}
                {a.hearing_history && (
                  <div><strong>Antecedentes auditivos:</strong> {a.hearing_history}</div>
                )}
                {a.notes && <div className="text-[#6E7772]">{a.notes}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function HearingAidsSection({ patientId, hearingAids, trials, onChange }) {
  const [open, setOpen] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [form, setForm] = useState({
    stock_item_id: "",
    brand: "",
    model: "",
    serial_number: "",
    ear: "izquierdo",
    loss_level: "",
    fitting_date: "",
    warranty_until: "",
    price: 0,
    notes: "",
    status: "activo",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/stock").then((r) => {
        const audifonos = (r.data || []).filter(
          (s) => s.type === "audifono" && !s.is_trial && (s.quantity || 0) > 0,
        );
        setStockItems(audifonos);
      }).catch(() => {});
    }
  }, [open]);

  const pickStock = (sid) => {
    if (sid === "none" || !sid) {
      setForm({ ...form, stock_item_id: "" });
      return;
    }
    const s = stockItems.find((x) => x.id === sid);
    if (!s) return;
    const serials = (s.serials || []).filter(Boolean);
    setForm({
      ...form,
      stock_item_id: s.id,
      brand: s.brand || form.brand,
      model: s.model || form.model,
      serial_number: serials[0] || s.serial_number || "",
      price: s.unit_price || form.price,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/hearing-aids`, {
        ...form,
        stock_item_id: form.stock_item_id || null,
        price: parseFloat(form.price) || 0,
      });
      toast.success("Audífono añadido");
      setOpen(false);
      setForm({
        stock_item_id: "",
        brand: "",
        model: "",
        serial_number: "",
        ear: "izquierdo",
        loss_level: "",
        fitting_date: "",
        warranty_until: "",
        price: 0,
        notes: "",
        status: "activo",
      });
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (hid) => {
    if (!window.confirm("¿Eliminar audífono?")) return;
    await api.delete(`/hearing-aids/${hid}`);
    toast.success("Eliminado");
    onChange();
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#6E7772]">
          Dispositivos vinculados al paciente: audífonos con marca, modelo y número de serie. Más abajo puedes asignar otros dispositivos (cargador, aparato TV, mando…).
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-hearing-aid-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Añadir audífono
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nuevo audífono</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Seleccionar de stock (rellena los datos automáticamente)</Label>
                <Select
                  value={form.stock_item_id || "none"}
                  onValueChange={pickStock}
                >
                  <SelectTrigger data-testid="hearing-aid-stock-select">
                    <SelectValue placeholder="— Sin asignar (rellenar manualmente) —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar — rellenar manualmente</SelectItem>
                    {stockItems.length === 0 && (
                      <SelectItem value="empty" disabled>
                        No hay audífonos disponibles en stock
                      </SelectItem>
                    )}
                    {stockItems.map((s) => {
                      const nSerials = (s.serials || []).filter(Boolean).length;
                      return (
                        <SelectItem key={s.id} value={s.id}>
                          {s.brand} {s.model}
                          {nSerials > 1
                            ? ` · ${nSerials} S/N disponibles`
                            : s.serial_number
                              ? ` · S/N ${s.serial_number}`
                              : ""}
                          {" — "}
                          {s.quantity} ud · {Number(s.unit_price || 0).toFixed(2)} €
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {form.stock_item_id && (
                  <div className="text-xs text-[#4A5D4E] mt-1">
                    La garantía se calculará automáticamente. Escoge abajo el número de serie de la unidad que has cogido.
                  </div>
                )}
              </div>
              <div>
                <Label>Marca *</Label>
                <Input
                  data-testid="hearing-aid-brand"
                  required
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                />
              </div>
              <div>
                <Label>Modelo *</Label>
                <Input
                  required
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                />
              </div>
              <div>
                <Label>Nº de serie</Label>
                {(() => {
                  const s = stockItems.find((x) => x.id === form.stock_item_id);
                  const serials = (s?.serials || []).filter(Boolean);
                  if (form.stock_item_id && serials.length > 0) {
                    return (
                      <>
                        <Select
                          value={form.serial_number || serials[0]}
                          onValueChange={(v) => setForm({ ...form, serial_number: v })}
                        >
                          <SelectTrigger data-testid="hearing-aid-serial-select">
                            <SelectValue placeholder="Escoge la unidad" />
                          </SelectTrigger>
                          <SelectContent>
                            {serials.map((sn) => (
                              <SelectItem key={sn} value={sn}>
                                {sn}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-[#6E7772] mt-1">
                          {serials.length} unidad(es) en stock — la escogida se descontará al guardar.
                        </p>
                      </>
                    );
                  }
                  return (
                    <Input
                      data-testid="hearing-aid-serial-input"
                      value={form.serial_number}
                      onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                    />
                  );
                })()}
              </div>
              <div>
                <Label>Oído</Label>
                <Select value={form.ear} onValueChange={(v) => setForm({ ...form, ear: v })}>
                  <SelectTrigger data-testid="hearing-aid-ear-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="izquierdo">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#2C3E8C]" />
                        Izquierdo (OI)
                      </span>
                    </SelectItem>
                    <SelectItem value="derecho">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#C0392B]" />
                        Derecho (OD)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nivel pérdida</Label>
                <Select value={form.loss_level} onValueChange={(v) => setForm({ ...form, loss_level: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="leve">Leve</SelectItem>
                    <SelectItem value="moderada">Moderada</SelectItem>
                    <SelectItem value="severa">Severa</SelectItem>
                    <SelectItem value="profunda">Profunda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha adaptación</Label>
                <Input
                  type="date"
                  value={form.fitting_date}
                  onChange={(e) => setForm({ ...form, fitting_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Garantía hasta</Label>
                <Input
                  type="date"
                  value={form.warranty_until}
                  onChange={(e) => setForm({ ...form, warranty_until: e.target.value })}
                />
              </div>
              <div>
                <Label>Precio (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Notas</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-hearing-aid-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Trial activo (audífono de prueba asignado) */}
      {trials && trials.length > 0 && (
        <div className="space-y-2">
          {trials.map((t) => {
            const ends = t.trial_ends_at ? new Date(t.trial_ends_at) : null;
            const daysLeft = ends ? Math.ceil((ends - new Date()) / (1000 * 60 * 60 * 24)) : null;
            return (
              <Card
                key={t.id}
                className="border-[#C07F67] bg-[#FDF3EE] shadow-none"
                data-testid={`trial-${t.id}`}
              >
                <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs tracking-wider uppercase font-semibold text-[#C07F67]">
                      Trial en curso
                    </div>
                    <div className="font-heading text-lg mt-0.5">
                      {t.brand} <span className="text-[#6E7772]">{t.model}</span>
                    </div>
                    <div className="text-xs text-[#6E7772]">
                      S/N: {t.serial_number || "—"} · Inicio {formatDate(t.trial_started_at)}
                      {ends && (
                        <>
                          {" · Fin "}
                          {formatDate(t.trial_ends_at)}
                          {daysLeft !== null && (
                            <span className={daysLeft < 0 ? "text-[#C07F67] ml-1" : "ml-1"}>
                              ({daysLeft >= 0 ? `${daysLeft} días restantes` : `vencido hace ${-daysLeft} días`})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.post(`/stock/${t.id}/return-trial`);
                        toast.success("Trial devuelto");
                        onChange();
                      } catch (err) {
                        toast.error(formatApiError(err));
                      }
                    }}
                    data-testid={`return-trial-${t.id}`}
                  >
                    Devolver al stock
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {hearingAids.length === 0 ? (
        <Card className="border-[#E5E3DB] shadow-none bg-white">
          <CardContent className="p-12 text-center text-[#6E7772]">
            <Ear strokeWidth={1.5} className="h-8 w-8 mx-auto mb-2 opacity-60" />
            Sin audífonos registrados.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {hearingAids.map((h) => {
            const warrantyDate = h.warranty_until ? new Date(h.warranty_until) : null;
            const inWarranty = warrantyDate && warrantyDate > new Date();
            return (
            <Card key={h.id} className="border-[#E5E3DB] shadow-none bg-white">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading font-medium text-lg">
                      {h.brand} <span className="text-[#6E7772]">{h.model}</span>
                    </div>
                    <div className="text-xs text-[#6E7772]">
                      S/N: {h.serial_number || "—"}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(h.id)}
                    data-testid={`delete-hearing-aid-${h.id}`}
                    className="text-[#C07F67] hover:bg-[#F3E5D8]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {(() => {
                    const e = String(h.ear || "").toLowerCase();
                    const isLeft = e === "izquierdo" || e === "left" || e === "oi";
                    const isRight = e === "derecho" || e === "right" || e === "od";
                    return (
                      <Badge
                        className={
                          isLeft
                            ? "bg-[#2C3E8C] text-white"
                            : isRight
                              ? "bg-[#C0392B] text-white"
                              : "bg-[#EAE6DF] text-[#2A2F2C]"
                        }
                        data-testid={`hearing-aid-ear-badge-${h.id}`}
                      >
                        Oído {isLeft ? "izquierdo (OI)" : isRight ? "derecho (OD)" : (h.ear || "—")}
                      </Badge>
                    );
                  })()}
                  {h.loss_level && (
                    <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">{h.loss_level}</Badge>
                  )}
                  {h.fitting_date && (
                    <Badge variant="outline" className="border-[#E5E3DB]">
                      Adapt. {formatDate(h.fitting_date)}
                    </Badge>
                  )}
                  {h.warranty_until && (
                    <Badge
                      className={
                        inWarranty
                          ? "bg-[#D1E0D7] text-[#2A2F2C]"
                          : "bg-[#E8D1D1] text-[#2A2F2C]"
                      }
                      data-testid={`warranty-status-${h.id}`}
                    >
                      {inWarranty ? "En garantía" : "Fuera de garantía"} · hasta {formatDate(h.warranty_until)}
                    </Badge>
                  )}
                </div>
                {h.price > 0 && (
                  <div className="text-sm">
                    <strong>{euro(h.price)}</strong>
                  </div>
                )}
                {h.notes && <div className="text-sm text-[#6E7772]">{h.notes}</div>}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

const IVA_RATES = [21, 10, 4, 0];

// PaymentBadge compartido: importado desde @/components/PaymentBadge

// --------------------------------------------------------------------------- #
// Audiogramas — visor sencillo + sync con Noah ES
// --------------------------------------------------------------------------- #
const AUDIOGRAM_FREQS = [125, 250, 500, 1000, 2000, 4000, 8000];

function AudiogramChart({ left = [], right = [] }) {
  // SVG simple: eje X = frecuencias, eje Y = dB HL (0..120, invertido)
  const width = 460;
  const height = 240;
  const padX = 36;
  const padY = 18;
  const maxDb = 120;
  const xFor = (idx) => padX + (idx * (width - padX * 2)) / (AUDIOGRAM_FREQS.length - 1);
  const yFor = (db) => padY + (Math.max(-10, Math.min(maxDb, db)) + 10) * ((height - padY * 2) / (maxDb + 10));

  const pointFor = (arr) =>
    AUDIOGRAM_FREQS.map((f, i) => {
      const p = (arr || []).find((x) => Number(x.freq) === f);
      if (!p) return null;
      return { x: xFor(i), y: yFor(Number(p.threshold)), db: p.threshold };
    });
  const leftPts = pointFor(left);
  const rightPts = pointFor(right);

  const linePath = (pts) => {
    const valid = pts.filter(Boolean);
    if (valid.length === 0) return "";
    return valid.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-[520px] bg-white border border-[#E5E3DB] rounded">
      {/* Grid horizontal cada 20 dB */}
      {[-10, 0, 20, 40, 60, 80, 100, 120].map((db) => (
        <g key={db}>
          <line x1={padX} y1={yFor(db)} x2={width - padX} y2={yFor(db)} stroke="#E5E3DB" strokeWidth={db === 0 ? 1.4 : 0.6} />
          <text x={padX - 6} y={yFor(db) + 3} textAnchor="end" fontSize="9" fill="#6E7772">
            {db}
          </text>
        </g>
      ))}
      {/* Grid vertical en cada frecuencia */}
      {AUDIOGRAM_FREQS.map((f, i) => (
        <g key={f}>
          <line x1={xFor(i)} y1={padY} x2={xFor(i)} y2={height - padY} stroke="#E5E3DB" strokeWidth={0.6} />
          <text x={xFor(i)} y={padY - 4} textAnchor="middle" fontSize="9" fill="#6E7772">
            {f >= 1000 ? `${f / 1000}k` : f}
          </text>
        </g>
      ))}
      {/* Línea oído derecho (rojo, O) */}
      <path d={linePath(rightPts)} stroke="#C0392B" strokeWidth={1.4} fill="none" />
      {rightPts.filter(Boolean).map((p, i) => (
        <circle key={`r${i}`} cx={p.x} cy={p.y} r={5} stroke="#C0392B" strokeWidth={1.4} fill="none" />
      ))}
      {/* Línea oído izquierdo (azul, X) */}
      <path d={linePath(leftPts)} stroke="#2C3E8C" strokeWidth={1.4} fill="none" />
      {leftPts.filter(Boolean).map((p, i) => (
        <g key={`l${i}`} stroke="#2C3E8C" strokeWidth={1.4}>
          <line x1={p.x - 4} y1={p.y - 4} x2={p.x + 4} y2={p.y + 4} />
          <line x1={p.x - 4} y1={p.y + 4} x2={p.x + 4} y2={p.y - 4} />
        </g>
      ))}
    </svg>
  );
}

function AudiogramsSection({ patientId, audiograms, user, onChange }) {
  const [syncing, setSyncing] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [thresholds, setThresholds] = useState(() =>
    AUDIOGRAM_FREQS.reduce((acc, f) => ({ ...acc, [f]: { L: "", R: "" } }), {}),
  );
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const canEdit = ["admin", "gerente", "audioprotesista"].includes(user?.role);

  const syncNoah = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/audiograms/sync-noah`);
      toast.success(`Sincronizados ${data.synced} audiogramas desde Noah ES`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const resetManual = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setThresholds(AUDIOGRAM_FREQS.reduce((acc, f) => ({ ...acc, [f]: { L: "", R: "" } }), {}));
    setNotes("");
  };

  const submitManual = async (e) => {
    e.preventDefault();
    const left_ear = AUDIOGRAM_FREQS
      .filter((f) => thresholds[f].L !== "")
      .map((f) => ({ freq: f, threshold: parseFloat(thresholds[f].L) }));
    const right_ear = AUDIOGRAM_FREQS
      .filter((f) => thresholds[f].R !== "")
      .map((f) => ({ freq: f, threshold: parseFloat(thresholds[f].R) }));
    if (left_ear.length === 0 && right_ear.length === 0) {
      toast.error("Introduce al menos un umbral");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/audiograms`, { date, left_ear, right_ear, notes });
      toast.success("Audiograma guardado");
      setOpenManual(false);
      resetManual();
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (aid) => {
    if (!window.confirm("¿Eliminar este audiograma?")) return;
    try {
      await api.delete(`/audiograms/${aid}`);
      toast.success("Audiograma eliminado");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="audiograms-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-[#6E7772]">
          Audiogramas del paciente. Sincroniza con Noah ES o registra uno manualmente.
        </p>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={syncNoah}
              disabled={syncing}
              variant="outline"
              data-testid="sync-noah-audiograms-btn"
              className="border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#EAE6DF]"
            >
              {syncing ? "Sincronizando…" : "Sincronizar desde Noah"}
            </Button>
            <Dialog open={openManual} onOpenChange={(o) => { setOpenManual(o); if (!o) resetManual(); }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-audiogram-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  <Plus className="h-4 w-4 mr-2" /> Audiograma manual
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl bg-white">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Nuevo audiograma manual</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitManual} className="space-y-4">
                  <div className="rounded-md border border-[#E5E3DB] bg-[#FAF9F6] px-3 py-2 text-xs text-[#6E7772]">
                    Convención: <span className="text-[#C0392B] font-semibold">OD = rojo</span> · <span className="text-[#2C3E8C] font-semibold">OI = azul</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Fecha</Label>
                      <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid="audiogram-date-input"
                      />
                    </div>
                    <div>
                      <Label>Notas</Label>
                      <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                  </div>
                  <div className="border border-[#E5E3DB] rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#FAF9F6]">
                          <TableHead className="w-24">Frec. (Hz)</TableHead>
                          <TableHead className="text-[#C0392B]">
                            <span className="font-semibold">OD</span> · dB <span className="text-xs">(rojo)</span>
                          </TableHead>
                          <TableHead className="text-[#2C3E8C]">
                            <span className="font-semibold">OI</span> · dB <span className="text-xs">(azul)</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {AUDIOGRAM_FREQS.map((f) => (
                          <TableRow key={f} className="border-[#E5E3DB]">
                            <TableCell className="text-sm font-mono">{f}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="5"
                                min={-10}
                                max={120}
                                value={thresholds[f].R}
                                onChange={(e) =>
                                  setThresholds({ ...thresholds, [f]: { ...thresholds[f], R: e.target.value } })
                                }
                                data-testid={`audiogram-r-${f}`}
                                placeholder="—"
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="5"
                                min={-10}
                                max={120}
                                value={thresholds[f].L}
                                onChange={(e) =>
                                  setThresholds({ ...thresholds, [f]: { ...thresholds[f], L: e.target.value } })
                                }
                                data-testid={`audiogram-l-${f}`}
                                placeholder="—"
                                className="h-8"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpenManual(false)}>Cancelar</Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      data-testid="save-audiogram-btn"
                      className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                    >
                      {saving ? "Guardando…" : "Guardar audiograma"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {audiograms.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E5E3DB] bg-white p-10 text-center text-[#6E7772]">
          Aún no hay audiogramas registrados para este paciente.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {audiograms.map((a) => (
            <Card key={a.id} className="border-[#E5E3DB] bg-white" data-testid={`audiogram-card-${a.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-base">
                      {formatDate(a.date)}
                    </CardTitle>
                    <div className="text-xs text-[#6E7772] mt-1">
                      Fuente: {a.source === "noah" ? (
                        <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">Noah ES</Badge>
                      ) : (
                        <Badge className="bg-[#EAE6DF] text-[#2A2F2C]">Manual</Badge>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(a.id)}
                      className="text-[#C07F67]"
                      data-testid={`del-audiogram-${a.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-1">
                <AudiogramChart left={a.left_ear} right={a.right_ear} />
                {a.notes && (
                  <div className="mt-2 text-xs text-[#6E7772]">
                    <strong>Notas:</strong> {a.notes}
                  </div>
                )}
                <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-wider text-[#6E7772]">
                  <div><span className="text-[#C0392B] font-bold">●</span> <span className="text-[#C0392B] font-semibold">OD</span> — oído derecho (rojo)</div>
                  <div><span className="text-[#2C3E8C] font-bold">●</span> <span className="text-[#2C3E8C] font-semibold">OI</span> — oído izquierdo (azul)</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoicesSection({ patient, invoices, quotes, hearingAids = [], onChange, user }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("invoice"); // 'invoice' | 'quote'
  const isAdmin = user?.role === "admin" || user?.role === "center_admin";
  const canReturn = isAdmin || user?.role === "gerente";
  const [returnInvoice, setReturnInvoice] = useState(null);
  const [returnForm, setReturnForm] = useState({
    refund_amount: 0,
    serial: "",
    reason: "",
    restock_status: "reasignable",
    notes: "",
  });
  // Diálogo "marcar como pagada" — registra la forma de pago (y plazos si aplica)
  const [payTarget, setPayTarget] = useState(null);
  // Cuotas propuestas editables (cantidad y fecha de cada cuota)
  const [schedule, setSchedule] = useState([]);
  const [schedTouched, setSchedTouched] = useState(false);

  const addPeriod = (dateStr, freq, times) => {
    const d = new Date((dateStr || new Date().toISOString().slice(0, 10)) + "T00:00:00");
    if (freq === "semanal") d.setDate(d.getDate() + 7 * times);
    else if (freq === "quincenal") d.setDate(d.getDate() + 15 * times);
    else if (freq === "bimestral") d.setMonth(d.getMonth() + 2 * times);
    else if (freq === "trimestral") d.setMonth(d.getMonth() + 3 * times);
    else d.setMonth(d.getMonth() + times);
    return d.toISOString().slice(0, 10);
  };
  const buildSchedule = (invTotal, cuotaAmt, freq, firstDue) => {
    if (!(cuotaAmt > 0) || !(invTotal > 0)) return [];
    const n = Math.ceil(Math.round((invTotal / cuotaAmt) * 1e6) / 1e6);
    const rows = [];
    for (let i = 0; i < n; i++) {
      const amount = i < n - 1
        ? cuotaAmt
        : Math.round((invTotal - cuotaAmt * (n - 1)) * 100) / 100;
      if (amount <= 0) continue;
      rows.push({ due_date: addPeriod(firstDue, freq, i), amount });
    }
    return rows;
  };
  const [payForm, setPayForm] = useState({
    method: "efectivo",        // efectivo|tarjeta|transferencia|bizum|plazos_interno|plazos_financiacion
    installment_method: "",    // cómo se cobra cada plazo (solo plazos interno)
    frequency: "mensual",      // semanal|quincenal|mensual|bimestral|trimestral
    installment_amount: "",
    first_due: "",
  });
  useEffect(() => {
    setSchedTouched(false);
    setSchedule([]);
    // eslint-disable-next-line
  }, [payForm.frequency, payForm.installment_amount, payForm.first_due, payTarget?.id]);
  const [items, setItems] = useState([
    { description: "", quantity: 1, unit_price: 0, iva_rate: 21, discount_percent: 0, stock_item_id: null, hearing_aid_id: null },
  ]);
  const [defaultIva, setDefaultIva] = useState(21);
  const [notes, setNotes] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [ivaCategories, setIvaCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  // Descuentos predefinidos + custom
  const [discountType, setDiscountType] = useState("none"); // none|familiar|amigos|fiel|custom
  const [customDiscount, setCustomDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  // Financiación (start/end) — solo si payment_method = financiacion
  const [financingStart, setFinancingStart] = useState("");
  const [financingEnd, setFinancingEnd] = useState("");
  const [financingNotes, setFinancingNotes] = useState("");
  const [financingProvider, setFinancingProvider] = useState(""); // "centro" | "banco"
  const [financingMonthly, setFinancingMonthly] = useState(""); // €/mes
  // Serial concreto seleccionado por línea (clave = índice de items)
  const [serialByIdx, setSerialByIdx] = useState({});
  // Edición de factura/presupuesto existente: {type: 'invoice'|'quote', id, number}
  const [editTarget, setEditTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertLinks, setConvertLinks] = useState({});
  const [convertStock, setConvertStock] = useState([]);
  const [converting, setConverting] = useState(false);

  const openConvert = async (q) => {
    try {
      const isStockMgr = ["admin", "center_admin", "gerente"].includes(user?.role);
      const stockEndpoint = isStockMgr ? "/stock" : "/stock/available";
      const { data } = await api.get(stockEndpoint, { params: { center_id: q.center_id } });
      setConvertStock(
        (data || []).filter((s) => s.center_id === q.center_id && !s.is_trial && (s.quantity || 0) > 0),
      );
    } catch {
      setConvertStock([]);
    }
    setConvertLinks({});
    setConvertTarget(q);
  };

  const submitConvert = async () => {
    setConverting(true);
    try {
      const links = Object.entries(convertLinks)
        .filter(([, v]) => v?.stock_item_id)
        .map(([idx, v]) => ({ index: parseInt(idx), stock_item_id: v.stock_item_id, serial: v.serial || null }));
      const { data } = await api.post(`/quotes/${convertTarget.id}/convert`, { links });
      toast.success(`Convertido en factura ${data.invoice_number}`);
      setConvertTarget(null);
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConverting(false);
    }
  };
  // Total editado manualmente (recalcula los precios unitarios)
  const [targetTotal, setTargetTotal] = useState("");
  // Fecha del documento (editable)
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  // Accesorios asignados al paciente (facturables como los audífonos)
  const [accessories, setAccessories] = useState([]);
  useEffect(() => {
    if (!patient?.id) return;
    api.get(`/patients/${patient.id}/accessories`).then((r) => setAccessories(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [patient?.id, invoices.length]);

  useEffect(() => {
    if (open) {
      const isStockMgr = ["admin", "center_admin", "gerente"].includes(user?.role);
      const stockEndpoint = isStockMgr ? "/stock" : "/stock/available";
      Promise.all([
        api.get("/settings").then((r) => r.data).catch(() => ({})),
        api
          .get(stockEndpoint, { params: { center_id: patient.center_id } })
          .then((r) => r.data)
          .catch(() => []),
      ]).then(([settings, stk]) => {
        setCatalog(settings?.hearing_aid_catalog || []);
        setIvaCategories(settings?.iva_categories || []);
        const def = parseFloat(settings?.default_iva_rate);
        if (!Number.isNaN(def)) setDefaultIva(def);
        // sólo stock con disponibilidad y del centro del paciente
        setStockItems(
          (stk || []).filter(
            (s) => s.center_id === patient.center_id && !s.is_trial && (s.quantity || 0) > 0,
          ),
        );
      });
    }
  }, [open, patient?.center_id, user?.role]);

  // PRECIOS CON IVA INCLUIDO (PVP): el desglose Base/IVA se calcula hacia atrás
  // Descuento por línea (0-100) aplicado ANTES del descuento global
  const lineDiscPct = (it) => Math.max(0, Math.min(100, parseFloat(it.discount_percent) || 0));
  const lineGross = (it) =>
    (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0) * (1 - lineDiscPct(it) / 100);
  const grossRaw = items.reduce(
    (s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
    0,
  );
  const grossAfterLines = items.reduce((s, it) => s + lineGross(it), 0);
  const lineDiscountTotal = grossRaw - grossAfterLines;
  // Si el paciente tiene minusvalía, IVA forzado a 4% en TODAS las líneas
  const forcedIvaRate = patient?.disability ? 4 : null;
  // Resolver porcentaje real del descuento global
  const DISCOUNT_PRESETS = { familiar: 30, amigos: 15, fiel: 20 };
  const discountPct = discountType === "none"
    ? 0
    : discountType === "custom"
      ? Math.max(0, Math.min(100, parseFloat(customDiscount) || 0))
      : DISCOUNT_PRESETS[discountType] || 0;
  const discountAmount = grossAfterLines * (discountPct / 100);
  const total = grossAfterLines - discountAmount;
  const ivaByRate = items.reduce((acc, it) => {
    const g = lineGross(it) * (1 - discountPct / 100);
    const rate = forcedIvaRate !== null ? forcedIvaRate : (parseFloat(it.iva_rate) || 0);
    if (!acc[rate]) acc[rate] = 0;
    acc[rate] += g - g / (1 + rate / 100);
    return acc;
  }, {});
  const ivaTotal = Object.values(ivaByRate).reduce((a, b) => a + b, 0);
  const subtotal = total - ivaTotal; // base imponible

  const reset = () => {
    setItems([
      { description: "", quantity: 1, unit_price: 0, iva_rate: defaultIva, discount_percent: 0, stock_item_id: null, hearing_aid_id: null, accessory_id: null },
    ]);
    setNotes("");
    setDiscountType("none");
    setCustomDiscount(0);
    setTargetTotal("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod("");
    setFinancingStart("");
    setFinancingEnd("");
    setFinancingNotes("");
    setFinancingProvider("");
    setFinancingMonthly("");
    setSerialByIdx({});
    setEditTarget(null);
  };

  const startEdit = (doc, type) => {
    setMode(type);
    setDocDate((doc.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
    const consumed = doc.consumed_serials || [];
    const serialMapByIdx = {};
    const mapped = (doc.items || []).map((it, idx) => {
      if (it.stock_item_id) {
        const c = consumed.find((x) => x.stock_item_id === it.stock_item_id);
        if (c?.serial) serialMapByIdx[idx] = c.serial;
      }
      return {
        description: it.description || "",
        quantity: it.quantity ?? 1,
        unit_price: it.unit_price ?? 0,
        iva_rate: it.iva_rate ?? 21,
        discount_percent: it.discount_percent ?? 0,
        stock_item_id: it.stock_item_id || null,
        hearing_aid_id: it.hearing_aid_id || null,
        accessory_id: it.accessory_id || null,
        catalog_key: it.catalog_key || null,
      };
    });
    setItems(
      mapped.length
        ? mapped
        : [{ description: "", quantity: 1, unit_price: 0, iva_rate: defaultIva, discount_percent: 0, stock_item_id: null, hearing_aid_id: null, accessory_id: null }],
    );
    setSerialByIdx(serialMapByIdx);
    setNotes(doc.notes || "");
    setDiscountType(doc.discount_type || "none");
    setCustomDiscount(doc.discount_type === "custom" ? doc.discount_percent || 0 : 0);
    if (doc.date) setDocDate(String(doc.date).slice(0, 10));
    if (doc.iva_rate != null) setDefaultIva(doc.iva_rate);
    if (type === "invoice") {
      setPaymentMethod(doc.payment_method || "");
      setFinancingStart(doc.financing_start || "");
      setFinancingEnd(doc.financing_end || "");
      setFinancingNotes(doc.financing_notes || "");
      setFinancingProvider(doc.financing_provider || "");
      setFinancingMonthly(doc.financing_monthly_amount || "");
    } else {
      setPaymentMethod("");
    }
    setEditTarget({
      type,
      id: doc.id,
      number: type === "invoice" ? doc.invoice_number : doc.quote_number,
    });
    setOpen(true);
  };

  const deleteInvoice = async (inv) => {
    if (
      !window.confirm(
        `¿Eliminar definitivamente la factura ${inv.invoice_number}?\n\n• Los artículos volverán al stock\n• Se eliminarán sus cuotas de financiación\n• Si no es la última factura del año, quedará un hueco en la numeración`,
      )
    )
      return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Factura ${inv.invoice_number} eliminada`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`¿Eliminar el presupuesto ${q.quote_number}?`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success(`Presupuesto ${q.quote_number} eliminado`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Audífonos del paciente disponibles para facturar (no devueltos)
  const billableAids = (hearingAids || []).filter(
    (h) => h.status !== "devuelto" && h.status !== "anulado",
  );

  const earInfo = (rawEar) => {
    const e = String(rawEar || "").toLowerCase();
    if (e === "izquierdo" || e === "left" || e === "oi") return { side: "L", label: "OI", color: "#2C3E8C" };
    if (e === "derecho" || e === "right" || e === "od") return { side: "R", label: "OD", color: "#C0392B" };
    return { side: "?", label: rawEar || "—", color: "#9CA3AF" };
  };

  // Ajustar el TOTAL a mano: reparte el cambio proporcionalmente entre los
  // precios unitarios de las líneas (los precios se adaptan al nuevo total)
  const applyTargetTotal = () => {
    const tt = parseFloat(targetTotal);
    if (!(tt > 0) || grossAfterLines <= 0) {
      toast.error("Indica un total válido");
      return;
    }
    const factor = tt / grossAfterLines;
    const updated = items.map((it) => ({
      ...it,
      unit_price: Math.round((parseFloat(it.unit_price) || 0) * factor * 100) / 100,
    }));
    const newGross = updated.reduce((s, it) => s + lineGross(it), 0);
    const diff = Math.round((tt - newGross) * 100) / 100;
    if (Math.abs(diff) >= 0.01 && updated.length) {
      let bigIdx = 0;
      let bigVal = -1;
      updated.forEach((it, i) => {
        const v = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
        if (v > bigVal) { bigVal = v; bigIdx = i; }
      });
      const q = parseFloat(updated[bigIdx].quantity) || 1;
      const ld = 1 - lineDiscPct(updated[bigIdx]) / 100 || 1;
      updated[bigIdx] = {
        ...updated[bigIdx],
        unit_price: Math.round(((parseFloat(updated[bigIdx].unit_price) || 0) + diff / (q * ld)) * 100) / 100,
      };
    }
    setItems(updated);
    setTargetTotal("");
    toast.success("Precios unitarios ajustados al nuevo total");
  };

  // Accesorios asignados facturables
  const billableAccessories = accessories || [];

  const pickAccessory = (idx, aid) => {
    const a = billableAccessories.find((x) => x.id === aid);
    if (!a) return;
    const copy = [...items];
    const typeToCategory = {
      audifono: "audifonos", accesorio: "accesorios", pila: "pilas", molde: "moldes",
      tapon_bano_medida: "tapones", tapon_bano_standard: "tapones",
      tapon_ruido_medida: "tapones", tapon_ruido_standard: "tapones",
      reparacion: "reparaciones", otro: "otros",
    };
    const catKey = typeToCategory[a.category] || "accesorios";
    const catRate = ivaCategories.find((c) => c.key === catKey)?.iva_rate;
    const lineIva = catRate != null ? parseFloat(catRate) : 21;
    copy[idx] = {
      ...copy[idx],
      accessory_id: a.id,
      hearing_aid_id: null,
      stock_item_id: null,
      description: `${a.brand || ""} ${a.model || ""}`.trim() + (a.serial_number ? ` · S/N ${a.serial_number}` : "") || a.category || "Accesorio",
      unit_price: a.price || 0,
      iva_rate: lineIva,
      iva_category: catKey,
    };
    setItems(copy);
    setSerialByIdx((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const pickHearingAid = (idx, haid) => {
    const h = billableAids.find((x) => x.id === haid);
    if (!h) return;
    const copy = [...items];
    // IVA según categoría "audifonos" definida por el admin (fallback 10%)
    const catRate = ivaCategories.find((c) => c.key === "audifonos")?.iva_rate;
    const lineIva = catRate != null ? parseFloat(catRate) : 10;
    const ei = earInfo(h.ear);
    copy[idx] = {
      ...copy[idx],
      hearing_aid_id: h.id,
      stock_item_id: null,
      accessory_id: null,
      description: `${h.brand || ""} ${h.model || ""}`.trim() + ` · ${ei.label}` + (h.serial_number ? ` · S/N ${h.serial_number}` : ""),
      unit_price: h.price || 0,
      iva_rate: lineIva,
      iva_category: "audifonos",
    };
    setItems(copy);
    setSerialByIdx((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const pickStock = (idx, sid) => {
    const s = stockItems.find((x) => x.id === sid);
    if (!s) return;
    const copy = [...items];
    // El IVA viene de la categoría (basada en s.type). Fallback: IVA por defecto.
    // Categorías esperadas: "audifonos" / "accesorios" / "pilas" / "moldes" / "tapones" / "reparaciones" / "otros"
    const typeToCategory = {
      audifono: "audifonos",
      accesorio: "accesorios",
      pila: "pilas",
      molde: "moldes",
      tapon_bano_medida: "tapones",
      tapon_bano_standard: "tapones",
      tapon_ruido_medida: "tapones",
      tapon_ruido_standard: "tapones",
      reparacion: "reparaciones",
      otro: "otros",
    };
    const catKey = typeToCategory[s.type] || "otros";
    const catRate = ivaCategories.find((c) => c.key === catKey)?.iva_rate;
    const lineIva = catRate != null ? parseFloat(catRate) : (s.type === "audifono" ? 10 : defaultIva);
    copy[idx] = {
      ...copy[idx],
      stock_item_id: s.id,
      hearing_aid_id: null,
      description: `${s.brand || ""} ${s.model || ""}`.trim() || s.description || s.type,
      unit_price: s.unit_price || 0,
      iva_rate: lineIva,
      iva_category: catKey,
    };
    setItems(copy);
    // Auto-seleccionar primer serial disponible si los hay
    const serials = Array.isArray(s.serials) ? s.serials.filter(Boolean) : [];
    setSerialByIdx((prev) => ({
      ...prev,
      [idx]: serials.length > 0 ? serials[0] : (s.serial_number || ""),
    }));
  };

  const setSerialForIdx = (idx, serial) => {
    setSerialByIdx((prev) => ({ ...prev, [idx]: serial }));
  };

  const submit = async (e) => {
    e.preventDefault();
    // No-admin: factura desde stock; presupuesto desde catálogo
    if (!isAdmin) {
      if (mode === "quote") {
        if (items.some((i) => !i.catalog_key && !i.stock_item_id && !i.hearing_aid_id && !i.accessory_id)) {
          toast.error("Selecciona los productos desde el catálogo para emitir el presupuesto.");
          return;
        }
      } else if (items.some((i) => !i.stock_item_id && !i.hearing_aid_id && !i.accessory_id)) {
        toast.error("Selecciona los productos desde el stock para emitir la factura.");
        return;
      }
    }
    if (!items.some((i) => (i.description || "").trim())) {
      toast.error("Añade al menos una línea con descripción");
      return;
    }
    setSaving(true);
    try {
      // Construir mapa { stock_item_id: serial } — solo aplica a facturas
      const serialMap = {};
      if (mode === "invoice") {
        items.forEach((it, idx) => {
          const s = serialByIdx[idx];
          if (it.stock_item_id && s) {
            serialMap[it.stock_item_id] = s;
          }
        });
      }
      const payload = {
        patient_id: patient.id,
        center_id: patient.center_id,
        items: items
          .filter((i) => (i.description || "").trim())
          .map((i) => ({
            description: i.description,
            quantity: parseFloat(i.quantity) || 1,
            unit_price: parseFloat(i.unit_price) || 0,
            iva_rate: parseFloat(i.iva_rate) || 0,
            discount_percent: Math.max(0, Math.min(100, parseFloat(i.discount_percent) || 0)),
            stock_item_id: i.stock_item_id || null,
            hearing_aid_id: i.hearing_aid_id || null,
            accessory_id: i.accessory_id || null,
            catalog_key: i.catalog_key || null,
          })),
        date: docDate || undefined,
        iva_rate: parseFloat(defaultIva) || 0,
        notes,
        discount_type: discountType,
        discount_percent: discountType === "custom" ? (parseFloat(customDiscount) || 0) : 0,
      };
      const ttParsed = parseFloat(targetTotal);
      const targetActive = !isNaN(ttParsed) && ttParsed > 0;
      if (targetActive) {
        payload.target_total = ttParsed;
        payload.discount_type = "none";
        payload.discount_percent = 0;
      }
      if (Object.keys(serialMap).length > 0) {
        payload.serial_consumed_by_item = serialMap;
      }
      if (mode === "invoice" && paymentMethod) {
        payload.payment_method = paymentMethod;
        if (paymentMethod === "financiacion") {
          if (financingStart) payload.financing_start = financingStart;
          if (financingEnd) payload.financing_end = financingEnd;
          if (financingNotes) payload.financing_notes = financingNotes;
          if (financingProvider) payload.financing_provider = financingProvider;
          if (financingMonthly) {
            const m = parseFloat(financingMonthly);
            if (!isNaN(m) && m > 0) payload.financing_monthly_amount = m;
          }
        }
      }
      const endpoint = mode === "quote" ? "/quotes" : "/invoices";
      let data;
      if (editTarget) {
        const resp =
          editTarget.type === "quote"
            ? await api.patch(`/quotes/${editTarget.id}`, payload)
            : await api.put(`/invoices/${editTarget.id}`, payload);
        data = resp.data;
        toast.success(
          editTarget.type === "quote"
            ? `Presupuesto ${editTarget.number} actualizado`
            : `Factura ${editTarget.number} actualizada`,
        );
        if (data?.edit_warning) toast.warning(data.edit_warning, { duration: 9000 });
      } else {
        const resp = await api.post(endpoint, payload);
        data = resp.data;
        toast.success(
          mode === "quote"
            ? `Presupuesto ${data.quote_number} emitido`
            : `Factura ${data.invoice_number} emitida`,
        );
      }
      setOpen(false);
      reset();
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async (inv) => {
    try {
      const resp = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pickFromCatalog = (idx, presetIdx) => {
    if (presetIdx === "" || presetIdx == null) return;
    const preset = catalog[parseInt(presetIdx)];
    if (!preset) return;
    const catKey = preset.iva_category || (preset.type === "audifono" || !preset.type ? "audifonos" : null);
    const catRate = catKey ? ivaCategories.find((c) => c.key === catKey)?.iva_rate : null;
    const copy = [...items];
    copy[idx] = {
      ...copy[idx],
      description: `${preset.brand} ${preset.model}`,
      unit_price: preset.price,
      stock_item_id: null,
      hearing_aid_id: null,
      accessory_id: null,
      catalog_key: `${preset.brand}||${preset.model}`,
      ...(catRate != null ? { iva_rate: parseFloat(catRate) } : {}),
    };
    setItems(copy);
    setSerialByIdx((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <p className="text-sm text-[#6E7772]">
          Facturas y presupuestos del paciente. Numeración automática anual e IVA por línea.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setMode("quote");
              reset();
              setOpen(true);
            }}
            data-testid="new-quote-btn"
            className="border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#EAE6DF]"
          >
            <Plus className="h-4 w-4 mr-2" /> Presupuesto
          </Button>
          <Button
            onClick={() => {
              setMode("invoice");
              reset();
              setOpen(true);
            }}
            data-testid="new-invoice-btn"
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Emitir factura
          </Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editTarget
                  ? `Editar ${editTarget.type === "quote" ? "presupuesto" : "factura"} ${editTarget.number}`
                  : mode === "quote" ? "Nuevo presupuesto" : "Nueva factura"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 scrollbar-thin">
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider font-semibold text-[#6E7772] px-1">
                  <div className="col-span-4">Concepto</div>
                  <div className="col-span-1 text-right">Cant.</div>
                  <div className="col-span-2 text-right">Precio</div>
                  <div className="col-span-2 text-right">Dto %</div>
                  <div className="col-span-2 text-right">IVA</div>
                  <div className="col-span-1"></div>
                </div>
                {items.map((it, idx) => {
                  const lockedPrice = !isAdmin;
                  const requireStock = !isAdmin;
                  return (
                  <div key={idx} className="space-y-1">
                    {requireStock && mode !== "quote" && (
                      <div className="pl-1 pb-1">
                        <Select
                          value={it.accessory_id ? `acc:${it.accessory_id}` : (it.hearing_aid_id ? `ha:${it.hearing_aid_id}` : (it.stock_item_id ? `st:${it.stock_item_id}` : ""))}
                          onValueChange={(v) => {
                            if (v.startsWith("ha:")) pickHearingAid(idx, v.slice(3));
                            else if (v.startsWith("acc:")) pickAccessory(idx, v.slice(4));
                            else if (v.startsWith("st:")) pickStock(idx, v.slice(3));
                          }}
                        >
                          <SelectTrigger data-testid={`invoice-product-select-${idx}`} className="bg-white">
                            <SelectValue placeholder="Selecciona audífono, accesorio asignado o producto del stock…" />
                          </SelectTrigger>
                          <SelectContent>
                            {billableAids.length === 0 && stockItems.length === 0 && billableAccessories.length === 0 && (
                              <SelectItem value="__empty__" disabled>
                                Sin audífonos adaptados ni stock disponible
                              </SelectItem>
                            )}
                            {billableAids.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6E7772] bg-[#FAF9F6] border-b border-[#E5E3DB]">
                                  Audífonos adaptados a este paciente
                                </div>
                                {billableAids.map((h) => {
                                  const ei = earInfo(h.ear);
                                  return (
                                  <SelectItem key={`ha-${h.id}`} value={`ha:${h.id}`}>
                                    <span className="inline-flex items-center gap-1.5">
                                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: ei.color }} />
                                      {(h.brand || "—") + " " + (h.model || "")}{" "}
                                      <span className="text-[#6E7772]">
                                        · {ei.label}
                                        {h.serial_number ? ` · S/N ${h.serial_number}` : ""}
                                        {h.price ? ` · ${h.price.toFixed(2)} €` : ""}
                                      </span>
                                    </span>
                                  </SelectItem>
                                  );
                                })}
                              </>
                            )}
                            {billableAccessories.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6E7772] bg-[#FAF9F6] border-b border-t border-[#E5E3DB]">
                                  Accesorios asignados a este paciente
                                </div>
                                {billableAccessories.map((a) => (
                                  <SelectItem key={`acc-${a.id}`} value={`acc:${a.id}`}>
                                    {(a.brand || "—") + " " + (a.model || "")}
                                    {a.serial_number ? ` · S/N ${a.serial_number}` : ""}
                                    {a.price ? ` · ${Number(a.price).toFixed(2)} €` : ""}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                            {stockItems.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6E7772] bg-[#FAF9F6] border-b border-t border-[#E5E3DB]">
                                  Stock disponible del centro
                                </div>
                                {stockItems.map((s) => (
                                  <SelectItem key={`st-${s.id}`} value={`st:${s.id}`}>
                                    {(s.brand || "—") + " " + (s.model || "")} · {s.unit_price?.toFixed?.(2) || "0,00"} € · qty {s.quantity}
                                    {s.serial_number ? ` · S/N ${s.serial_number}` : ""}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <Input
                        className="col-span-4"
                        placeholder="Concepto"
                        value={it.description}
                        onChange={(e) => {
                          if (requireStock) return; // no editable para no-admin
                          const copy = [...items];
                          copy[idx].description = e.target.value;
                          setItems(copy);
                        }}
                        readOnly={requireStock}
                        data-testid={`invoice-item-desc-${idx}`}
                      />
                      <Input
                        className="col-span-1"
                        type="number"
                        step="0.01"
                        placeholder="1"
                        value={it.quantity}
                        onChange={(e) => {
                          const copy = [...items];
                          copy[idx].quantity = e.target.value;
                          setItems(copy);
                        }}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        step="0.01"
                        placeholder="0,00"
                        value={it.unit_price}
                        readOnly={lockedPrice}
                        onChange={(e) => {
                          if (lockedPrice) return;
                          const copy = [...items];
                          copy[idx].unit_price = e.target.value;
                          setItems(copy);
                        }}
                        data-testid={`invoice-item-price-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        min={0}
                        max={100}
                        step="0.5"
                        placeholder="0"
                        title="Descuento % de esta línea"
                        value={it.discount_percent ?? 0}
                        onChange={(e) => {
                          const copy = [...items];
                          copy[idx].discount_percent = e.target.value;
                          setItems(copy);
                        }}
                        data-testid={`invoice-item-discount-${idx}`}
                      />
                      <div className="col-span-2">
                        {isAdmin ? (
                          <Select
                            value={String(it.iva_rate ?? defaultIva)}
                            onValueChange={(v) => {
                              const copy = [...items];
                              copy[idx].iva_rate = parseFloat(v);
                              setItems(copy);
                            }}
                          >
                            <SelectTrigger data-testid={`invoice-item-iva-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {IVA_RATES.map((r) => (
                                <SelectItem key={r} value={String(r)}>
                                  {r}%
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div
                            className="h-9 px-3 rounded-md border border-[#E5E3DB] bg-[#F7F6F3] text-sm flex items-center justify-end text-[#2A2F2C]"
                            title={patient?.disability
                              ? "IVA 4 % por discapacidad"
                              : "IVA fijado por el administrador según grupo"}
                            data-testid={`invoice-item-iva-readonly-${idx}`}
                          >
                            {forcedIvaRate !== null ? forcedIvaRate : (parseFloat(it.iva_rate) || 0)}%
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="col-span-1"
                        onClick={() => setItems(items.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Selector de número de serie individual cuando el stock tiene >1 serial (solo facturas) */}
                    {mode !== "quote" && it.stock_item_id && (() => {
                      const s = stockItems.find((x) => x.id === it.stock_item_id);
                      const serials = (s?.serials || []).filter(Boolean);
                      if (serials.length === 0) return null;
                      return (
                        <div className="pl-1 flex items-center gap-2">
                          <span className="text-xs uppercase tracking-wider text-[#6E7772]">
                            Nº serie
                          </span>
                          <Select
                            value={serialByIdx[idx] || serials[0] || ""}
                            onValueChange={(v) => setSerialForIdx(idx, v)}
                          >
                            <SelectTrigger
                              data-testid={`invoice-serial-select-${idx}`}
                              className="h-7 text-xs bg-white max-w-[260px]"
                            >
                              <SelectValue placeholder="Selecciona el serial…" />
                            </SelectTrigger>
                            <SelectContent>
                              {serials.map((sn) => (
                                <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-[10px] text-[#6E7772]">
                            ({serials.length} disponibles en stock)
                          </span>
                        </div>
                      );
                    })()}
                    {/* Indicador visual de oído cuando la línea está asociada a un audífono (solo facturas) */}
                    {mode !== "quote" && it.hearing_aid_id && (() => {
                      const h = billableAids.find((x) => x.id === it.hearing_aid_id);
                      if (!h) return null;
                      const ei = earInfo(h.ear);
                      return (
                        <div className="pl-1 flex items-center gap-2 text-xs">
                          <Badge style={{ background: ei.color, color: "white" }}>
                            Oído {ei.side === "L" ? "izquierdo (OI)" : ei.side === "R" ? "derecho (OD)" : (h.ear || "—")}
                          </Badge>
                          <span className="text-[10px] text-[#6E7772]">
                            Audífono adaptado — no descontará stock
                          </span>
                        </div>
                      );
                    })()}
                    {/* Admin: dropdown opcional combinado (audífonos + accesorios + stock) — solo facturas */}
                    {isAdmin && mode !== "quote" && (billableAids.length > 0 || stockItems.length > 0 || billableAccessories.length > 0) && (
                      <div className="pl-1">
                        <Select
                          value={it.accessory_id ? `acc:${it.accessory_id}` : (it.hearing_aid_id ? `ha:${it.hearing_aid_id}` : (it.stock_item_id ? `st:${it.stock_item_id}` : ""))}
                          onValueChange={(v) => {
                            if (v.startsWith("ha:")) pickHearingAid(idx, v.slice(3));
                            else if (v.startsWith("acc:")) pickAccessory(idx, v.slice(4));
                            else if (v.startsWith("st:")) pickStock(idx, v.slice(3));
                          }}
                        >
                          <SelectTrigger
                            data-testid={`invoice-product-admin-${idx}`}
                            className="text-xs h-7 bg-transparent border-dashed"
                          >
                            <SelectValue placeholder="⮕ Enlazar con audífono adaptado, accesorio o stock…" />
                          </SelectTrigger>
                          <SelectContent>
                            {billableAids.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6E7772] bg-[#FAF9F6] border-b border-[#E5E3DB]">
                                  Audífonos adaptados a este paciente
                                </div>
                                {billableAids.map((h) => {
                                  const ei = earInfo(h.ear);
                                  return (
                                  <SelectItem key={`adm-ha-${h.id}`} value={`ha:${h.id}`}>
                                    <span className="inline-flex items-center gap-1.5">
                                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: ei.color }} />
                                      {(h.brand || "—") + " " + (h.model || "")} · {ei.label}
                                      {h.price ? ` · ${h.price.toFixed(2)} €` : ""}
                                    </span>
                                  </SelectItem>
                                  );
                                })}
                              </>
                            )}
                            {billableAccessories.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6E7772] bg-[#FAF9F6] border-b border-t border-[#E5E3DB]">
                                  Accesorios asignados a este paciente
                                </div>
                                {billableAccessories.map((a) => (
                                  <SelectItem key={`acc-${a.id}`} value={`acc:${a.id}`}>
                                    {(a.brand || "—") + " " + (a.model || "")}
                                    {a.serial_number ? ` · S/N ${a.serial_number}` : ""}
                                    {a.price ? ` · ${Number(a.price).toFixed(2)} €` : ""}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                            {stockItems.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6E7772] bg-[#FAF9F6] border-b border-t border-[#E5E3DB]">
                                  Stock disponible del centro
                                </div>
                                {stockItems.map((s) => (
                                  <SelectItem key={`adm-st-${s.id}`} value={`st:${s.id}`}>
                                    {(s.brand || "—") + " " + (s.model || "")} · {s.unit_price?.toFixed?.(2) || "0,00"} € · qty {s.quantity}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {mode === "quote" && (
                      <div className="pl-1 pb-1 flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-[#6E7772] whitespace-nowrap">Catálogo</span>
                        <Select
                          value={(() => {
                            if (!it.catalog_key) return "";
                            const ci = catalog.findIndex((c) => `${c.brand}||${c.model}` === it.catalog_key);
                            return ci >= 0 ? String(ci) : "";
                          })()}
                          onValueChange={(v) => pickFromCatalog(idx, v)}
                        >
                          <SelectTrigger data-testid={`quote-catalog-select-${idx}`} className="bg-white h-8">
                            <SelectValue placeholder="Selecciona un producto del catálogo…" />
                          </SelectTrigger>
                          <SelectContent>
                            {catalog.length === 0 && (
                              <SelectItem value="__empty__" disabled>
                                Catálogo vacío — añade productos en Configuración → Catálogo
                              </SelectItem>
                            )}
                            {catalog.map((c, i) => (
                              <SelectItem key={i} value={String(i)}>
                                {c.brand} {c.model} — {Number(c.price).toFixed(2)} €
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );})}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setItems([
                      ...items,
                      { description: "", quantity: 1, unit_price: 0, iva_rate: defaultIva, discount_percent: 0, stock_item_id: null, hearing_aid_id: null },
                    ])
                  }
                >
                  <Plus className="h-4 w-4 mr-2" /> Añadir línea
                </Button>
              </div>

              <div>
                <Label>Observaciones</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              {patient?.disability && (
                <div className="bg-[#D1E0D7] border border-[#4A5D4E] rounded-md px-4 py-2 text-sm text-[#2A2F2C]" data-testid="disability-banner">
                  <strong>♿ Paciente con minusvalía</strong> — el IVA aplicado será 4% en TODAS las líneas automáticamente.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Descuento</Label>
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger data-testid="invoice-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin descuento</SelectItem>
                      <SelectItem value="familiar">Familiar (30%)</SelectItem>
                      <SelectItem value="fiel">Fiel (20%)</SelectItem>
                      <SelectItem value="amigos">Amigos (15%)</SelectItem>
                      <SelectItem value="custom">Personalizado…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {discountType === "custom" && (
                  <div>
                    <Label>% descuento personalizado</Label>
                    <Input
                      data-testid="invoice-discount-custom"
                      type="number"
                      min={0}
                      max={100}
                      step="0.5"
                      value={customDiscount}
                      onChange={(e) => setCustomDiscount(e.target.value)}
                    />
                  </div>
                )}
                {mode === "invoice" && (
                  <div>
                    <Label>Forma de pago {!isAdmin && <span className="text-[10px] text-[#C07F67]">(solo visible para administrador)</span>}</Label>
                    <Select value={paymentMethod || "none"} onValueChange={(v) => setPaymentMethod(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="invoice-payment-method"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Sin especificar —</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="bizum">Bizum</SelectItem>
                        <SelectItem value="financiacion">Financiación</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Bloque de financiación — solo cuando forma de pago = financiacion */}
              {mode === "invoice" && paymentMethod === "financiacion" && (
                <div
                  data-testid="invoice-financing-block"
                  className="rounded-md border border-[#C9B79C] bg-[#FBF7EF] p-4 space-y-3"
                >
                  <div className="text-xs uppercase tracking-wider font-semibold text-[#7C5A2A]">
                    Datos de la financiación
                  </div>
                  <div>
                    <Label>Financiada por *</Label>
                    <Select
                      value={financingProvider || "none"}
                      onValueChange={(v) => setFinancingProvider(v === "none" ? "" : v)}
                    >
                      <SelectTrigger data-testid="financing-provider-select">
                        <SelectValue placeholder="Selecciona…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Seleccionar —</SelectItem>
                        <SelectItem value="centro">El centro (cobro mensual interno)</SelectItem>
                        <SelectItem value="banco">Banco / entidad financiera</SelectItem>
                      </SelectContent>
                    </Select>
                    {financingProvider === "centro" && (
                      <p className="text-[11px] text-[#7C5A2A] mt-1">
                        Se generará automáticamente un aviso de cobro mensual hasta la fecha de fin.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Inicio</Label>
                      <Input
                        type="date"
                        data-testid="financing-start-input"
                        value={financingStart}
                        onChange={(e) => setFinancingStart(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Fin</Label>
                      <Input
                        type="date"
                        data-testid="financing-end-input"
                        value={financingEnd}
                        onChange={(e) => setFinancingEnd(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Cuota mensual (€) <span className="text-[10px] text-[#6E7772]">(opcional — si no, se reparte total/meses)</span></Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        data-testid="financing-monthly-input"
                        value={financingMonthly}
                        onChange={(e) => setFinancingMonthly(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Notas de financiación</Label>
                      <Input
                        data-testid="financing-notes-input"
                        value={financingNotes}
                        onChange={(e) => setFinancingNotes(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Fecha {mode === "quote" ? "del presupuesto" : "de la factura"}</Label>
                  <Input
                    type="date"
                    value={docDate}
                    onChange={(e) => setDocDate(e.target.value)}
                    data-testid="invoice-date-input"
                  />
                </div>
              </div>

              <div className="rounded-md border border-[#E5E3DB] bg-[#F7F6F3] p-4 text-sm">
                {lineDiscountTotal > 0.004 && (
                  <>
                    <div className="flex justify-between">
                      <span>Importe bruto (IVA incl.)</span>
                      <span>{euro(grossRaw)}</span>
                    </div>
                    <div className="flex justify-between text-[#C07F67]" data-testid="invoice-line-discount-total">
                      <span>Dto. por líneas</span>
                      <span>-{euro(lineDiscountTotal)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span>Importe (IVA incl.)</span>
                  <span>{euro(grossAfterLines)}</span>
                </div>
                {discountPct > 0.0001 && (
                  <div className="flex justify-between text-[#C07F67]" data-testid="invoice-discount-line">
                    <span>Descuento {Math.round(discountPct * 100) / 100}%</span>
                    <span>-{euro(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Base imponible</span>
                  <span>{euro(subtotal)}</span>
                </div>
                {Object.entries(ivaByRate)
                  .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                  .map(([rate, amount]) => (
                    <div key={rate} className="flex justify-between text-[#6E7772]">
                      <span>IVA {rate}% (incluido)</span>
                      <span>{euro(amount)}</span>
                    </div>
                  ))}
                <div className="flex justify-between font-heading text-lg text-[#4A5D4E] mt-2 border-t border-[#E5E3DB] pt-2">
                  <span>Total</span>
                  <span data-testid="invoice-total">{euro(total)}</span>
                </div>
                {isAdmin && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-[#E5E3DB]">
                    <span className="text-xs text-[#6E7772]">
                      Ajustar TOTAL (recalcula los precios unitarios)
                    </span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        className="w-28 h-8 text-right bg-white"
                        placeholder={grossAfterLines > 0 ? total.toFixed(2) : "0.00"}
                        value={targetTotal}
                        onChange={(e) => setTargetTotal(e.target.value)}
                        data-testid="invoice-target-total"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs"
                        disabled={!(parseFloat(targetTotal) > 0)}
                        onClick={applyTargetTotal}
                        data-testid="apply-target-total-btn"
                      >
                        Aplicar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid={mode === "quote" ? "emit-quote-btn" : "emit-invoice-btn"}
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving
                    ? editTarget ? "Guardando…" : "Emitiendo…"
                    : editTarget
                      ? "Guardar cambios"
                      : mode === "quote" ? "Emitir presupuesto" : "Emitir factura"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Diálogo: convertir presupuesto en factura eligiendo stock y nº de serie */}
        <Dialog open={!!convertTarget} onOpenChange={(v) => { if (!v) setConvertTarget(null); }}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Convertir {convertTarget?.quote_number} en factura
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <p className="text-sm text-[#6E7772]">
                Vincula cada línea con el artículo del stock y elige su número de serie. Las líneas vinculadas descontarán stock al emitir la factura; las no vinculadas pasan tal cual.
              </p>
              {(convertTarget?.items || []).map((it, idx) => {
                const link = convertLinks[idx] || {};
                const st = convertStock.find((x) => x.id === link.stock_item_id);
                const serials = (st?.serials || []).filter(Boolean);
                return (
                  <div key={idx} className="rounded-md border border-[#E5E3DB] p-3 space-y-2">
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="font-medium">{it.description}</span>
                      <span className="text-[#6E7772] whitespace-nowrap">{it.quantity} × {euro(it.unit_price)}</span>
                    </div>
                    <Select
                      value={link.stock_item_id || "none"}
                      onValueChange={(v) =>
                        setConvertLinks((prev) => ({
                          ...prev,
                          [idx]:
                            v === "none"
                              ? {}
                              : {
                                  stock_item_id: v,
                                  serial: ((convertStock.find((x) => x.id === v)?.serials || []).filter(Boolean))[0] || "",
                                },
                        }))
                      }
                    >
                      <SelectTrigger data-testid={`convert-stock-select-${idx}`} className="bg-white h-9">
                        <SelectValue placeholder="— No vincular con stock —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No vincular con stock —</SelectItem>
                        {convertStock.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {(s.brand || "—") + " " + (s.model || "")} · {Number(s.unit_price || 0).toFixed(2)} € · qty {s.quantity}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {serials.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider text-[#6E7772]">Nº serie</span>
                        <Select
                          value={link.serial || serials[0]}
                          onValueChange={(v) => setConvertLinks((prev) => ({ ...prev, [idx]: { ...prev[idx], serial: v } }))}
                        >
                          <SelectTrigger data-testid={`convert-serial-select-${idx}`} className="h-8 text-xs bg-white max-w-[260px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {serials.map((sn) => (
                              <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-[10px] text-[#6E7772]">({serials.length} disponibles)</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConvertTarget(null)} data-testid="convert-cancel-btn">
                Cancelar
              </Button>
              <Button
                onClick={submitConvert}
                disabled={converting}
                className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                data-testid="convert-confirm-btn"
              >
                {converting ? "Convirtiendo…" : "Convertir en factura"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quotes section */}
      {quotes && quotes.length > 0 && (
        <div>
          <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
            Presupuestos emitidos
          </div>
          <div className="rounded-lg border border-[#E5E3DB] bg-white">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E3DB]">
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Válido hasta</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((q) => (
                  <TableRow key={q.id} className="border-[#E5E3DB]" data-testid={`patient-quote-${q.id}`}>
                    <TableCell className="font-mono text-sm">{q.quote_number}</TableCell>
                    <TableCell>{formatDate(q.date)}</TableCell>
                    <TableCell className="text-[#6E7772]">{formatDate(q.valid_until)}</TableCell>
                    <TableCell>
                      {user?.role === "gerente" ? (
                        <Badge
                          className={
                            q.status === "aceptado" || q.status === "convertido"
                              ? "bg-[#D1E0D7] text-[#2A2F2C]"
                              : q.status === "rechazado"
                              ? "bg-[#E8D1D1] text-[#2A2F2C]"
                              : "bg-[#EAE6DF] text-[#2A2F2C]"
                          }
                        >
                          {q.status}
                        </Badge>
                      ) : (
                        <Select
                          value={q.status}
                          onValueChange={async (v) => {
                            if (v === q.status) return;
                            try {
                              await api.patch(`/quotes/${q.id}/status`, null, { params: { status: v } });
                              toast.success(`Presupuesto marcado como ${v}`);
                              onChange();
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          disabled={q.status === "convertido"}
                        >
                          <SelectTrigger
                            data-testid={`quote-status-${q.id}`}
                            className="h-8 w-36 bg-white border-[#E5E3DB]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="borrador">Borrador</SelectItem>
                            <SelectItem value="aceptado">Aceptado</SelectItem>
                            <SelectItem value="rechazado">Rechazado</SelectItem>
                            {q.status === "convertido" && (
                              <SelectItem value="convertido" disabled>Convertido</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">{euro(q.total)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const resp = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
                              const url = URL.createObjectURL(resp.data);
                              window.open(url, "_blank");
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          data-testid={`quote-pdf-${q.id}`}
                        >
                          <Printer className="h-4 w-4 mr-1" /> A4
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const resp = await api.get(`/quotes/${q.id}/pdf-template`, { responseType: "blob" });
                                const url = URL.createObjectURL(resp.data);
                                const a = document.createElement("a");
                                a.href = url;
                                const ct = resp.headers["content-type"] || "";
                                const ext = ct.includes("wordprocessingml") ? "docx" : "pdf";
                                a.download = `presupuesto-${q.quote_number}.${ext}`;
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                if (err?.response?.status === 404) {
                                  toast.error("Este centro no tiene plantilla activa para presupuestos.");
                                } else {
                                  toast.error(formatApiError(err));
                                }
                              }
                            }}
                            data-testid={`quote-template-${q.id}`}
                            title="Generar con plantilla del centro"
                          >
                            <FileText className="h-4 w-4 mr-1" /> Plantilla
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const resp = await api.get(`/quotes/${q.id}/ticket`, { responseType: "blob" });
                              const url = URL.createObjectURL(resp.data);
                              window.open(url, "_blank");
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          data-testid={`quote-ticket-${q.id}`}
                          title="Ticket 80mm (impresora térmica)"
                        >
                          <Printer className="h-4 w-4 mr-1" /> Ticket
                        </Button>
                        {q.status !== "convertido" && q.status !== "rechazado" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`quote-convert-${q.id}`}
                            title="Convertir este presupuesto en factura"
                            onClick={() => openConvert(q)}
                            className="text-[#4A5D4E] hover:bg-[#EAE6DF]"
                          >
                            <FileCheck2 className="h-4 w-4 mr-1" /> Convertir
                          </Button>
                        )}
                        {q.status === "convertido" && q.converted_invoice_id && (
                          <Badge className="bg-[#D1E0D7] text-[#1F2A24] text-[10px]" data-testid={`quote-converted-${q.id}`}>
                            → Factura
                          </Badge>
                        )}
                        {canReturn && q.status !== "convertido" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(q, "quote")}
                            data-testid={`quote-edit-${q.id}`}
                            title="Editar presupuesto"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canReturn && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteQuote(q)}
                            data-testid={`quote-delete-${q.id}`}
                            title="Eliminar presupuesto"
                          >
                            <Trash2 className="h-4 w-4 text-[#C07F67]" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div>
        {quotes && quotes.length > 0 && (
          <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E7772] mb-2">
            Facturas
          </div>
        )}
        {invoices.length === 0 ? (
          <Card className="border-[#E5E3DB] shadow-none bg-white">
            <CardContent className="p-12 text-center text-[#6E7772]">
              <FileText strokeWidth={1.5} className="h-8 w-8 mx-auto mb-2 opacity-60" />
              Sin facturas todavía.
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-lg border border-[#E5E3DB] bg-white">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E3DB]">
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha</TableHead>
                  {isAdmin && <TableHead>Forma de pago</TableHead>}
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-[#E5E3DB]" data-testid={`patient-invoice-${inv.id}`}>
                    <TableCell className="font-mono text-sm">
                      <div>{inv.invoice_number}</div>
                      {inv.origin_quote_number && (
                        <div className="text-[10px] text-[#6E7772] mt-0.5" data-testid={`invoice-origin-${inv.id}`}>
                          ← desde presup. <span className="font-semibold">{inv.origin_quote_number}</span>
                        </div>
                      )}
                      {inv.walk_in && (
                        <Badge className="bg-[#EAE6DF] text-[#3F4744] text-[9px] mt-0.5">Cliente varios</Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(inv.date)}</TableCell>
                    {isAdmin && (
                      <TableCell data-testid={`invoice-payment-${inv.id}`}>
                        <PaymentBadge inv={inv} />
                      </TableCell>
                    )}
                    <TableCell>
                      {user?.role === "gerente" ? (
                        <Badge
                          className={
                            inv.status === "pagada"
                              ? "bg-[#D1E0D7] text-[#2A2F2C]"
                              : inv.status === "anulada"
                              ? "bg-[#E8D1D1] text-[#2A2F2C]"
                              : inv.status === "a_plazos"
                              ? "bg-[#F3E5D8] text-[#7C5A2A]"
                              : "bg-[#F3E5D8] text-[#2A2F2C]"
                          }
                        >
                          {inv.status === "a_plazos" ? "a plazos" : inv.status}
                        </Badge>
                      ) : (
                        <Select
                          value={inv.status}
                          onValueChange={async (v) => {
                            if (v === inv.status) return;
                            if (v === "pagada") {
                              // Pedir la forma de pago antes de marcar como pagada
                              setPayTarget(inv);
                              setPayForm({
                                method: "efectivo",
                                installment_method: "",
                                frequency: "mensual",
                                installment_amount: "",
                                first_due: "",
                              });
                              return;
                            }
                            try {
                              await api.patch(`/invoices/${inv.id}/status`, null, { params: { status: v } });
                              toast.success(`Factura marcada como ${v}`);
                              onChange();
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                        >
                          <SelectTrigger
                            data-testid={`invoice-status-${inv.id}`}
                            className="h-8 w-36 bg-white border-[#E5E3DB]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="emitida">Emitida</SelectItem>
                            <SelectItem value="a_plazos">A plazos</SelectItem>
                            <SelectItem value="pagada">Pagada</SelectItem>
                            <SelectItem value="anulada">Anulada</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">{euro(inv.total)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPdf(inv)}
                          data-testid={`invoice-pdf-${inv.id}`}
                        >
                          <Printer className="h-4 w-4 mr-1" /> A4
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const resp = await api.get(`/invoices/${inv.id}/pdf-template`, { responseType: "blob" });
                                const url = URL.createObjectURL(resp.data);
                                const a = document.createElement("a");
                                a.href = url;
                                // Detectar tipo por content-type
                                const ct = resp.headers["content-type"] || "";
                                const ext = ct.includes("wordprocessingml") ? "docx" : "pdf";
                                a.download = `factura-${inv.invoice_number}.${ext}`;
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                if (err?.response?.status === 404) {
                                  toast.error("Este centro no tiene plantilla activa para facturas. Súbela en Ajustes → Plantillas.");
                                } else {
                                  toast.error(formatApiError(err));
                                }
                              }
                            }}
                            data-testid={`invoice-template-${inv.id}`}
                            title="Generar usando la plantilla activa del centro (.docx o .pdf)"
                          >
                            <FileText className="h-4 w-4 mr-1" /> Plantilla
                          </Button>
                        )}
                        {canReturn && (inv.status === "pagada" || (inv.total || 0) > 0) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const firstSerial = (inv.consumed_serials && inv.consumed_serials[0]) || "";
                              setReturnInvoice(inv);
                              setReturnForm({
                                refund_amount: inv.total || 0,
                                serial: firstSerial,
                                reason: "",
                                restock_status: "reasignable",
                                notes: "",
                              });
                            }}
                            data-testid={`return-invoice-${inv.id}`}
                            title="Devolución del paciente"
                          >
                            <Undo2 className="h-4 w-4 text-[#C07F67]" />
                          </Button>
                        )}
                        {inv.has_returns && (
                          <Badge className="bg-[#F3E5DC] text-[#8A5540] text-[10px]" title="Esta factura tiene devoluciones registradas">
                            Devuelto
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const resp = await api.get(`/invoices/${inv.id}/ticket`, { responseType: "blob" });
                              try {
                                if (await printTicketBlob(resp.data)) {
                                  toast.success("Ticket enviado a la impresora");
                                  return;
                                }
                              } catch (perr) {
                                toast.error(`Impresora: ${perr.message}. Abriendo PDF…`);
                              }
                              const url = URL.createObjectURL(resp.data);
                              window.open(url, "_blank");
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          data-testid={`invoice-ticket-${inv.id}`}
                          title="Ticket 80mm (impresora térmica)"
                        >
                          <Printer className="h-4 w-4 mr-1" /> Ticket
                        </Button>
                        {inv.verifactu_qr_url && (
                          <a
                            href={`${API_BASE}/verifactu/qr/${inv.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-xs text-[#4A5D4E] hover:underline"
                            data-testid={`invoice-qr-${inv.id}`}
                            title={`Veri*Factu · ${inv.verifactu_status || "pending"}`}
                          >
                            <FileCheck2 className="h-4 w-4" />
                            <span className="ml-0.5 hidden lg:inline">
                              {inv.verifactu_status === "accepted" ? "AEAT ✓" : inv.verifactu_status === "rejected" ? "AEAT ✗" : "QR"}
                            </span>
                          </a>
                        )}
                        {inv.verifactu_qr_url && user?.role === "admin" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                const resp = await api.get(`/verifactu/xml/${inv.id}`, { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([resp.data], { type: "application/xml" }));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `verifactu_${(inv.invoice_number || inv.id).replaceAll("/", "-")}.xml`;
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                toast.error(formatApiError(err));
                              }
                            }}
                            data-testid={`invoice-vf-xml-${inv.id}`}
                            title="Descargar XML firmado Veri*Factu"
                          >
                            <FileDown className="h-4 w-4" />
                            <span className="ml-0.5 hidden lg:inline text-xs">XML</span>
                          </Button>
                        )}
                        {canReturn && inv.status !== "anulada" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(inv, "invoice")}
                            data-testid={`invoice-edit-${inv.id}`}
                            title="Editar factura"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canReturn && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteInvoice(inv)}
                            data-testid={`invoice-delete-${inv.id}`}
                            title="Eliminar factura definitivamente"
                          >
                            <Trash2 className="h-4 w-4 text-[#C07F67]" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {isAdmin && invoices.some((i) => i.payment_method === "financiacion") && (
        <FinancingDetailsPanel patientId={patient.id} patient={patient} invoices={invoices} onChange={onChange} />
      )}

      {/* Devolución de artículo por parte del paciente */}
      <Dialog open={!!returnInvoice} onOpenChange={(v) => !v && setReturnInvoice(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="return-patient-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-[#C07F67]" strokeWidth={2} />
              Devolución del paciente
            </DialogTitle>
          </DialogHeader>
          {returnInvoice && (
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                const amount = parseFloat(returnForm.refund_amount) || 0;
                if (amount <= 0) {
                  toast.error("Importe a reembolsar debe ser mayor que 0");
                  return;
                }
                if (amount > (returnInvoice.total || 0)) {
                  toast.error("El reembolso excede el total de la factura");
                  return;
                }
                if (!window.confirm(
                  `¿Confirmar devolución?\n\nFactura: ${returnInvoice.invoice_number}\nReembolso: ${amount.toFixed(2)}€\nEstado stock: ${returnForm.restock_status}\n\nSe registrará y la factura se actualizará.`
                )) return;
                try {
                  await api.post(`/invoices/${returnInvoice.id}/return-item`, {
                    source_type: "patient",
                    quantity: 1,
                    serial: returnForm.serial || null,
                    reason: returnForm.reason || "",
                    restock_status: returnForm.restock_status,
                    notes: returnForm.notes || "",
                    refund_amount: amount,
                    patient_invoice_id: returnInvoice.id,
                    patient_id: patient.id,
                  });
                  toast.success("Devolución registrada");
                  setReturnInvoice(null);
                  onChange?.();
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
            >
              <div className="p-3 bg-[#F7F6F3] border border-[#E5E3DB] rounded text-sm">
                <div className="font-medium text-[#2A2F2C]">Factura {returnInvoice.invoice_number}</div>
                <div className="text-xs text-[#6E7772] mt-1">
                  Total actual: <span className="font-medium">{euro(returnInvoice.total)}</span> · {(returnInvoice.items || []).length} artículo(s)
                </div>
                {returnInvoice.consumed_serials?.length > 0 && (
                  <div className="text-xs text-[#6E7772] mt-1">
                    S/N vendidos: {returnInvoice.consumed_serials.join(", ")}
                  </div>
                )}
              </div>

              {returnInvoice.consumed_serials?.length > 0 && (
                <div>
                  <Label>Nº de serie devuelto</Label>
                  <Select
                    value={returnForm.serial || undefined}
                    onValueChange={(v) => setReturnForm({ ...returnForm, serial: v })}
                  >
                    <SelectTrigger data-testid="return-patient-serial">
                      <SelectValue placeholder="Selecciona serial" />
                    </SelectTrigger>
                    <SelectContent>
                      {returnInvoice.consumed_serials.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Importe a reembolsar (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  max={returnInvoice.total || 0}
                  value={returnForm.refund_amount}
                  onChange={(e) => setReturnForm({ ...returnForm, refund_amount: e.target.value })}
                  data-testid="return-patient-amount"
                />
                <p className="text-[11px] text-[#6E7772] mt-1">Se descontará del total de la factura.</p>
              </div>

              <div>
                <Label>Estado del artículo devuelto *</Label>
                <Select
                  value={returnForm.restock_status}
                  onValueChange={(v) => setReturnForm({ ...returnForm, restock_status: v })}
                >
                  <SelectTrigger data-testid="return-patient-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reasignable">Reasignable · vuelve al stock disponible</SelectItem>
                    <SelectItem value="defectuoso">Defectuoso · devolución al proveedor</SelectItem>
                    <SelectItem value="chatarra">Chatarra · baja definitiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Motivo</Label>
                <Select
                  value={returnForm.reason || undefined}
                  onValueChange={(v) => setReturnForm({ ...returnForm, reason: v })}
                >
                  <SelectTrigger data-testid="return-patient-reason">
                    <SelectValue placeholder="Selecciona motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_adaptacion">No se adapta</SelectItem>
                    <SelectItem value="defectuoso">Defectuoso / mal funcionamiento</SelectItem>
                    <SelectItem value="cambio_modelo">Cambio de modelo</SelectItem>
                    <SelectItem value="periodo_prueba">Devolución en periodo de prueba</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notas</Label>
                <Input
                  value={returnForm.notes}
                  onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })}
                  data-testid="return-patient-notes"
                  placeholder="Observaciones sobre la devolución"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReturnInvoice(null)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="confirm-patient-return-btn"
                  className="bg-[#C07F67] hover:bg-[#B06E56] text-white"
                >
                  Registrar devolución
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Registrar pago — forma de pago al marcar la factura como pagada */}
      <Dialog open={!!payTarget} onOpenChange={(v) => !v && setPayTarget(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Registrar pago · {payTarget?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {payTarget && (() => {
            const isPlazos = payForm.method === "plazos_interno" || payForm.method === "plazos_financiacion";
            const isLibre = payForm.frequency === "libre";
            const total = payTarget.total || 0;
            const cuota = parseFloat(payForm.installment_amount) || 0;
            const nCuotas = cuota > 0 ? Math.ceil(total / cuota) : 0;
            const ultima = nCuotas > 1 ? Math.round((total - cuota * (nCuotas - 1)) * 100) / 100 : cuota;
            return (
              <form
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (isPlazos && !isLibre && !(cuota > 0)) {
                    toast.error("Indica la cantidad de la cuota");
                    return;
                  }
                  if (payForm.method === "plazos_interno" && !isLibre && !payForm.installment_method) {
                    toast.error("Indica cómo pagará cada plazo");
                    return;
                  }
                  const body = { payment_method: isPlazos ? "financiacion" : payForm.method };
                  if (isPlazos) {
                    body.financing_provider = payForm.method === "plazos_interno" ? "centro" : "banco";
                    body.financing_payment_method = payForm.method === "plazos_interno" && !isLibre ? payForm.installment_method : null;
                    body.financing_frequency = payForm.frequency;
                    if (!isLibre) body.financing_installment_amount = cuota;
                    if (!isLibre && payForm.first_due) body.financing_start = payForm.first_due;
                    if (!isLibre && schedTouched && schedule.length) {
                      body.custom_schedule = schedule
                        .map((r) => ({ due_date: r.due_date, amount: parseFloat(r.amount) || 0 }))
                        .filter((r) => r.amount > 0 && r.due_date);
                    }
                  }
                  try {
                    await api.patch(`/invoices/${payTarget.id}/status`, body, { params: { status: "pagada" } });
                    toast.success("Factura marcada como pagada");
                    setPayTarget(null);
                    onChange();
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
              >
                <div className="p-3 bg-[#F7F6F3] border border-[#E5E3DB] rounded text-sm flex items-center justify-between">
                  <span>Total de la factura</span>
                  <strong className="text-[#2A2F2C]">{euro(total)}</strong>
                </div>

                <div>
                  <Label>Forma de pago *</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="bizum">Bizum</SelectItem>
                      <SelectItem value="plazos_interno">A plazos interno (lo cobra el centro)</SelectItem>
                      <SelectItem value="plazos_financiacion">A plazos por financiación (financiera/banco)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {payForm.method === "plazos_interno" && payForm.frequency !== "libre" && (
                  <div>
                    <Label>Cómo pagará cada plazo *</Label>
                    <Select
                      value={payForm.installment_method || undefined}
                      onValueChange={(v) => setPayForm({ ...payForm, installment_method: v })}
                    >
                      <SelectTrigger data-testid="pay-installment-method"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="bizum">Bizum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isPlazos && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className={payForm.frequency === "libre" ? "col-span-2" : ""}>
                      <Label>Cada cuánto pagarán *</Label>
                      <Select value={payForm.frequency} onValueChange={(v) => setPayForm({ ...payForm, frequency: v })}>
                        <SelectTrigger data-testid="pay-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="semanal">Cada semana</SelectItem>
                          <SelectItem value="quincenal">Cada 15 días</SelectItem>
                          <SelectItem value="mensual">Cada mes</SelectItem>
                          <SelectItem value="bimestral">Cada 2 meses</SelectItem>
                          <SelectItem value="trimestral">Cada 3 meses</SelectItem>
                          <SelectItem value="libre">Sin periodicidad — pagos libres</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {payForm.frequency !== "libre" && (
                      <div>
                        <Label>Cantidad de la cuota (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min={0}
                          value={payForm.installment_amount}
                          onChange={(e) => setPayForm({ ...payForm, installment_amount: e.target.value })}
                          data-testid="pay-installment-amount"
                        />
                      </div>
                    )}
                    {payForm.frequency !== "libre" && (
                      <div className="col-span-2">
                        <Label>Fecha del primer pago</Label>
                        <Input
                          type="date"
                          value={payForm.first_due}
                          onChange={(e) => setPayForm({ ...payForm, first_due: e.target.value })}
                          data-testid="pay-first-due"
                        />
                        <p className="text-[11px] text-[#6E7772] mt-1">Si se deja vacío, se usa la fecha de hoy.</p>
                      </div>
                    )}
                  </div>
                )}

                {isPlazos && isLibre && (
                  <div className="p-3 bg-[#FBF7EF] border border-[#C9B79C] rounded text-sm" data-testid="pay-free-info">
                    No se programan cuotas. Registra cada pago (fecha, modo de pago y cantidad) con el botón
                    «Registrar pago» del panel de financiación: cada pago emitirá su recibo y, al completar el
                    total, la factura pasará a «Pagada» automáticamente.
                  </div>
                )}

                {isPlazos && !isLibre && cuota > 0 && (() => {
                  const rows = schedTouched ? schedule : buildSchedule(total, cuota, payForm.frequency, payForm.first_due);
                  const sumRows = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                  const mismatch = Math.abs(sumRows - total) > 0.01;
                  const editRow = (i, key, val) => {
                    const base = schedTouched ? [...schedule] : rows.map((r) => ({ ...r }));
                    base[i] = { ...base[i], [key]: val };
                    setSchedule(base);
                    setSchedTouched(true);
                  };
                  return (
                    <div className="p-3 bg-[#FBF7EF] border border-[#C9B79C] rounded text-sm space-y-2" data-testid="pay-installments-info">
                      <div className="flex items-center justify-between">
                        <span>
                          <strong>{rows.length}</strong> cuota(s) — edita la cantidad o la fecha de cualquier cuota si lo necesitas
                        </span>
                        {schedTouched && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[11px] text-[#7C5A2A] underline"
                            onClick={() => { setSchedTouched(false); setSchedule([]); }}
                            data-testid="reset-schedule-btn"
                          >
                            Recalcular automáticamente
                          </Button>
                        )}
                      </div>
                      <div className="max-h-52 overflow-y-auto space-y-1">
                        {rows.map((r, i) => (
                          <div key={i} className="grid grid-cols-[2.5rem_1fr_1fr] gap-2 items-center">
                            <span className="text-xs font-mono text-[#7C5A2A]">{i + 1}/{rows.length}</span>
                            <Input
                              type="date"
                              className="h-8 bg-white"
                              value={r.due_date}
                              onChange={(e) => editRow(i, "due_date", e.target.value)}
                              data-testid={`schedule-date-${i}`}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              className="h-8 bg-white text-right"
                              value={r.amount}
                              onChange={(e) => editRow(i, "amount", e.target.value)}
                              data-testid={`schedule-amount-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className={`text-xs ${mismatch ? "text-[#C0392B] font-medium" : "text-[#7C5A2A]"}`} data-testid="schedule-sum">
                        Suma de cuotas: {euro(sumRows)} · Total factura: {euro(total)}
                        {mismatch && <> — ⚠ no cuadra (diferencia {euro(sumRows - total)}), puedes guardarlo igualmente</>}
                      </div>
                      {payForm.method === "plazos_interno" && (
                        <div className="text-[11px] text-[#7C5A2A]">
                          Se crearán los cobros pendientes con estas cuotas y la factura quedará en estado
                          «A plazos» hasta cobrar la última (entonces pasará a «Pagada» sola).
                        </div>
                      )}
                    </div>
                  );
                })()}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPayTarget(null)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="confirm-pay-btn"
                    className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                  >
                    Confirmar pago
                  </Button>
                </DialogFooter>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}

// --------------------------------------------------------------------------- //
// Panel de financiación (admin) — muestra cobros pendientes por factura
// --------------------------------------------------------------------------- //
function FinancingDetailsPanel({ patientId, patient, invoices, onChange }) {
  const waRemind = (c, inv) => {
    const phone = patient?.phone;
    if (!phone) return null;
    const msg = `Hola ${patient?.name || ""}, te recordamos que tu cuota de ${euro(c.amount)} (factura ${inv.invoice_number}) vence el ${formatDate(c.due_date)}. Un saludo.`;
    let p = String(phone).replace(/[^\d+]/g, "");
    if (p.startsWith("00")) p = "+" + p.slice(2);
    if (!p.startsWith("+") && p.length === 9) p = "+34" + p;
    return `https://wa.me/${p.replace("+", "")}?text=${encodeURIComponent(msg)}`;
  };
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  // Registro de pagos libres (sin periodicidad): fecha, modo de pago y cantidad
  const [payInv, setPayInv] = useState(null);
  const [payData, setPayData] = useState({ date: "", amount: "", method: "efectivo" });
  const [editColId, setEditColId] = useState(null);
  const [editColData, setEditColData] = useState({ amount: "", due_date: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/financing-collections", {
        params: { patient_id: patientId, status: "all" },
      });
      setCollections(data || []);
    } catch (err) {
      // Sin permisos o error
      setCollections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId, invoices.length]);

  const financed = invoices.filter((i) => i.payment_method === "financiacion");
  if (financed.length === 0) return null;

  const openReceipt = async (cid) => {
    try {
      const resp = await api.get(`/financing-collections/${cid}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const registerPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payData.amount) || 0;
    if (!(amount > 0)) {
      toast.error("Indica la cantidad del pago");
      return;
    }
    try {
      const { data } = await api.post(`/invoices/${payInv.id}/payments`, {
        date: payData.date || null,
        amount,
        payment_method: payData.method || null,
      });
      if (data?.invoice_paid) {
        toast.success("Pago registrado — la factura queda PAGADA");
      } else {
        toast.success(`Pago registrado. Pendiente: ${euro(data?.pending ?? 0)}`);
      }
      setPayInv(null);
      load();
      if (onChange) onChange();
      if (data?.collection_id) openReceipt(data.collection_id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveColEdit = async (cid) => {
    try {
      const body = {};
      if (editColData.amount !== "") body.amount = parseFloat(editColData.amount);
      if (editColData.due_date) body.due_date = editColData.due_date;
      const { data } = await api.patch(`/financing-collections/${cid}`, body);
      toast.success("Cuota actualizada");
      if (data?.warning) toast.warning(data.warning, { duration: 8000 });
      setEditColId(null);
      load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPaid = async (cid) => {
    if (!window.confirm("¿Marcar esta cuota como cobrada?")) return;
    try {
      const { data } = await api.post(`/financing-collections/${cid}/mark-paid`, {});
      if (data?.invoice_paid) {
        toast.success("Última cuota cobrada — la factura pasa a estado PAGADA");
      } else {
        toast.success("Cuota marcada como cobrada");
      }
      load();
      if (onChange) onChange();
      // Abrir directamente el recibo para entregarlo al paciente
      openReceipt(cid);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-3" data-testid="financing-details-panel">
      <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#7C5A2A]">
        Financiación
      </div>
      <div className="space-y-3">
        {financed.map((inv) => {
          const cols = collections.filter((c) => c.invoice_id === inv.id);
          const pending = cols.filter((c) => c.status === "pending").length;
          const paid = cols.filter((c) => c.status === "paid").length;
          return (
            <Card key={inv.id} className="border-[#C9B79C] bg-[#FBF7EF]">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="font-heading text-base">
                      Factura {inv.invoice_number} — {euro(inv.total)}
                    </CardTitle>
                    <div className="text-xs text-[#7C5A2A] mt-1">
                      {inv.financing_provider === "centro" ? "Centro" : inv.financing_provider === "banco" ? "Banco" : "Sin definir"}
                      {" · "}
                      {inv.financing_start ? formatDate(inv.financing_start) : "—"} → {inv.financing_end ? formatDate(inv.financing_end) : "—"}
                      {inv.financing_monthly_amount ? ` · ${euro(inv.financing_monthly_amount)}/mes` : inv.financing_frequency === "libre" ? " · pagos libres" : ""}
                    </div>
                    {inv.financing_notes && (
                      <div className="text-[11px] text-[#6E7772] mt-1">
                        Nota: {inv.financing_notes}
                      </div>
                    )}
                  </div>
                  {(inv.financing_provider === "centro" || cols.length > 0) && (
                    <div className="flex items-center gap-2 text-xs">
                      <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">{paid} cobradas</Badge>
                      <Badge className="bg-[#F3E5D8] text-[#7C5A2A]">{pending} pendientes</Badge>
                      {inv.financing_provider === "centro" && inv.status !== "pagada" && inv.status !== "anulada" && (
                        <Button
                          size="sm"
                          data-testid={`add-payment-${inv.id}`}
                          onClick={() => {
                            setPayInv(inv);
                            setPayData({ date: new Date().toISOString().slice(0, 10), amount: "", method: "efectivo" });
                          }}
                          className="h-7 text-xs bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                        >
                          Registrar pago
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              {cols.length > 0 && (
                <CardContent className="pt-1">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-white/50">
                        <TableHead className="w-16">Nº</TableHead>
                        <TableHead>Vencimiento</TableHead>
                        <TableHead className="text-right">Importe</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cols.map((c) => {
                        const today = new Date().toISOString().slice(0, 10);
                        const overdue = c.status === "pending" && c.due_date < today;
                        return (
                          <TableRow key={c.id} className="border-[#E5D8C0]" data-testid={`collection-${c.id}`}>
                            <TableCell className="text-sm font-mono">
                              {c.total_installments ? `${c.installment_no}/${c.total_installments}` : `Pago ${c.installment_no}`}
                            </TableCell>
                            <TableCell className={overdue ? "text-[#C0392B] font-medium" : ""}>
                              {editColId === c.id ? (
                                <Input
                                  type="date"
                                  className="h-8 w-36 bg-white"
                                  value={editColData.due_date}
                                  onChange={(e) => setEditColData({ ...editColData, due_date: e.target.value })}
                                  data-testid={`edit-col-date-${c.id}`}
                                />
                              ) : (
                                <>{formatDate(c.due_date)} {overdue && <Badge className="ml-2 bg-[#E8D1D1] text-[#2A2F2C]">Vencida</Badge>}</>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {editColId === c.id ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min={0}
                                  className="h-8 w-24 bg-white text-right ml-auto"
                                  value={editColData.amount}
                                  onChange={(e) => setEditColData({ ...editColData, amount: e.target.value })}
                                  data-testid={`edit-col-amount-${c.id}`}
                                />
                              ) : (
                                euro(c.amount)
                              )}
                            </TableCell>
                            <TableCell>
                              {c.status === "paid" ? (
                                <>
                                  <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">Cobrada</Badge>
                                  {c.payment_method && (
                                    <span className="text-[10px] text-[#6E7772] ml-1">{c.payment_method}</span>
                                  )}
                                </>
                              ) : c.status === "cancelled" ? (
                                <Badge className="bg-[#EAE6DF] text-[#6E7772]">Anulada</Badge>
                              ) : (
                                <Badge className="bg-[#F3E5D8] text-[#7C5A2A]">Pendiente</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {c.status === "pending" && editColId === c.id && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    data-testid={`save-col-${c.id}`}
                                    onClick={() => saveColEdit(c.id)}
                                    className="h-7 text-xs bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                                  >
                                    Guardar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditColId(null)}
                                    className="h-7 text-xs"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              )}
                              {c.status === "pending" && editColId !== c.id && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid={`mark-paid-${c.id}`}
                                    onClick={() => markPaid(c.id)}
                                    className="h-7 text-xs border-[#4A5D4E] text-[#4A5D4E]"
                                  >
                                    Marcar cobrada
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`edit-col-${c.id}`}
                                    title="Editar cantidad y fecha de esta cuota"
                                    onClick={() => {
                                      setEditColId(c.id);
                                      setEditColData({ amount: String(c.amount ?? ""), due_date: c.due_date || "" });
                                    }}
                                    className="h-7 w-7 p-0 text-[#7C5A2A]"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  {waRemind(c, inv) && (
                                    <a
                                      href={waRemind(c, inv)}
                                      target="_blank"
                                      rel="noreferrer"
                                      data-testid={`remind-col-${c.id}`}
                                      title="Recordar esta cuota al paciente por WhatsApp"
                                      className="h-7 w-7 inline-flex items-center justify-center rounded text-[#4A5D4E] hover:bg-[#E4EFE8] transition-colors"
                                    >
                                      <MessageCircle className="h-3.5 w-3.5" />
                                    </a>
                                  )}
                                </div>
                              )}
                              {c.status === "paid" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`receipt-${c.id}`}
                                  onClick={() => openReceipt(c.id)}
                                  title="Imprimir recibo de esta cuota para el paciente"
                                  className="h-7 text-xs border-[#7C5A2A] text-[#7C5A2A]"
                                >
                                  <Printer className="h-3 w-3 mr-1" /> Recibo
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
              {inv.financing_provider !== "centro" && cols.length === 0 && (
                <CardContent className="pt-1 text-xs text-[#6E7772]">
                  Financiada por banco/entidad sin cuadro de cuotas registrado. Las nuevas facturas financiadas por banco con periodicidad generan sus cuotas automáticamente.
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Registrar pago libre: fecha, modo de pago y cantidad → recibo */}
      <Dialog open={!!payInv} onOpenChange={(v) => !v && setPayInv(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="manual-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Registrar pago · {payInv?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {payInv && (() => {
            const paidCols = collections.filter((c) => c.invoice_id === payInv.id && c.status === "paid");
            const paidAmount = paidCols.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
            const pendingAmount = Math.max(0, Math.round(((payInv.total || 0) - paidAmount) * 100) / 100);
            return (
              <form className="space-y-4" onSubmit={registerPayment}>
                <div className="p-3 bg-[#F7F6F3] border border-[#E5E3DB] rounded text-sm flex items-center justify-between">
                  <span>Pendiente de pago</span>
                  <strong data-testid="manual-payment-pending">{euro(pendingAmount)}</strong>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Fecha del pago *</Label>
                    <Input
                      type="date"
                      value={payData.date}
                      onChange={(e) => setPayData({ ...payData, date: e.target.value })}
                      data-testid="manual-payment-date"
                    />
                  </div>
                  <div>
                    <Label>Cantidad (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={payData.amount}
                      onChange={(e) => setPayData({ ...payData, amount: e.target.value })}
                      data-testid="manual-payment-amount"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Modo de pago *</Label>
                    <Select value={payData.method} onValueChange={(v) => setPayData({ ...payData, method: v })}>
                      <SelectTrigger data-testid="manual-payment-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="bizum">Bizum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[11px] text-[#6E7772]">
                  Al confirmar se emitirá el recibo del pago para entregarlo al paciente. Cuando los pagos
                  cubran el total, la factura pasará a «Pagada» automáticamente.
                </p>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPayInv(null)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="confirm-manual-payment"
                    className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                  >
                    Confirmar y emitir recibo
                  </Button>
                </DialogFooter>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Repairs section
// --------------------------------------------------------------------------- //
const REPAIR_STATUS = [
  { value: "pendiente", label: "Pendiente envío" },
  { value: "enviado", label: "Enviado al SAT" },
  { value: "en_reparacion", label: "En reparación" },
  { value: "recibido", label: "Recibido" },
  { value: "entregado", label: "Entregado al paciente" },
  { value: "anulado", label: "Anulado" },
];

const STATUS_COLOR_REPAIR = {
  pendiente: "bg-[#EAE6DF] text-[#2A2F2C]",
  enviado: "bg-[#F3E5D8] text-[#2A2F2C]",
  en_reparacion: "bg-[#F3E5D8] text-[#2A2F2C]",
  recibido: "bg-[#D1E0D7] text-[#2A2F2C]",
  entregado: "bg-[#D1E0D7] text-[#2A2F2C]",
  anulado: "bg-[#E8D1D1] text-[#2A2F2C]",
};

function RepairsSection({ patient, repairs, hearingAids, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    hearing_aid_id: "",
    description: "",
    status: "pendiente",
    sent_date: "",
    received_date: "",
    delivered_date: "",
    cost: 0,
    notes: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patient.id}/repairs`, {
        ...form,
        hearing_aid_id: form.hearing_aid_id || null,
        cost: parseFloat(form.cost) || 0,
      });
      toast.success("Reparación registrada");
      setOpen(false);
      setForm({
        hearing_aid_id: "",
        description: "",
        status: "pendiente",
        sent_date: "",
        received_date: "",
        delivered_date: "",
        cost: 0,
        notes: "",
      });
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (r, status) => {
    try {
      const update = { ...r, status };
      if (status === "enviado" && !r.sent_date) update.sent_date = new Date().toISOString().slice(0, 10);
      if (status === "recibido" && !r.received_date) update.received_date = new Date().toISOString().slice(0, 10);
      if (status === "entregado" && !r.delivered_date) update.delivered_date = new Date().toISOString().slice(0, 10);
      await api.patch(`/repairs/${r.id}`, update);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar reparación?")) return;
    try {
      await api.delete(`/repairs/${r.id}`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#6E7772]">
          Seguimiento de reparaciones de audífonos: envío, recepción y entrega al paciente.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-repair-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Nueva reparación
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nueva reparación</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Descripción / motivo *</Label>
                <Input
                  data-testid="repair-description"
                  required
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="ej. Audífono no enciende, cambio de molde…"
                />
              </div>
              <div>
                <Label>Audífono asociado</Label>
                <Select
                  value={form.hearing_aid_id || "none"}
                  onValueChange={(v) => setForm({ ...form, hearing_aid_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asociar</SelectItem>
                    {hearingAids.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.brand} {h.model} {h.serial_number ? `· S/N ${h.serial_number}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="repair-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPAIR_STATUS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha envío</Label>
                <Input type="date" value={form.sent_date} onChange={(e) => setForm({ ...form, sent_date: e.target.value })} />
              </div>
              <div>
                <Label>Fecha recepción</Label>
                <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} />
              </div>
              <div>
                <Label>Coste (€)</Label>
                <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Notas</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-repair-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar reparación"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {repairs.length === 0 ? (
        <Card className="border-[#E5E3DB] shadow-none bg-white">
          <CardContent className="p-12 text-center text-[#6E7772]">
            Sin reparaciones registradas.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {repairs.map((r) => (
            <Card key={r.id} className="border-[#E5E3DB] shadow-none bg-white" data-testid={`repair-${r.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-[#2A2F2C]">{r.description}</span>
                      <Badge className={STATUS_COLOR_REPAIR[r.status] || "bg-[#EAE6DF]"}>
                        {REPAIR_STATUS.find((s) => s.value === r.status)?.label || r.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-[#6E7772] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {r.sent_date && <span>Enviado: {formatDate(r.sent_date)}</span>}
                      {r.received_date && <span>Recibido: {formatDate(r.received_date)}</span>}
                      {r.delivered_date && <span>Entregado: {formatDate(r.delivered_date)}</span>}
                      {r.cost > 0 && <span>Coste: {euro(r.cost)}</span>}
                    </div>
                    {r.notes && <div className="text-sm text-[#6E7772] mt-1">{r.notes}</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => remove(r)}>
                    <Trash2 className="h-4 w-4 text-[#C07F67]" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#E5E3DB]">
                  {REPAIR_STATUS.filter((s) => s.value !== "anulado").map((s) => (
                    <Button
                      key={s.value}
                      size="sm"
                      variant={r.status === s.value ? "default" : "outline"}
                      className={
                        r.status === s.value
                          ? "h-7 text-[11px] bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                          : "h-7 text-[11px]"
                      }
                      onClick={() => setStatus(r, s.value)}
                      data-testid={`repair-${r.id}-set-${s.value}`}
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
// History (visit log)
// --------------------------------------------------------------------------- //
function HistorySection({ patientId, visits, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reason: "", notes: "", visit_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/visits`, form);
      toast.success("Visita registrada");
      setOpen(false);
      setForm({ reason: "", notes: "", visit_date: new Date().toISOString().slice(0, 10) });
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm("¿Eliminar entrada del historial?")) return;
    await api.delete(`/visits/${v.id}`);
    onChange();
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#6E7772]">
          Registro automático y manual de cada visita al centro.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-visit-btn" className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white">
              <Plus className="h-4 w-4 mr-2" /> Registrar visita
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nueva visita</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Motivo *</Label>
                <Input
                  data-testid="visit-reason"
                  required
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="ej. Limpieza, ajuste, consulta…"
                />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={form.visit_date}
                  onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-visit-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {visits.length === 0 ? (
        <Card className="border-[#E5E3DB] shadow-none bg-white">
          <CardContent className="p-12 text-center text-[#6E7772]">
            Sin entradas en el historial todavía.
          </CardContent>
        </Card>
      ) : (
        <div className="relative pl-6 border-l-2 border-[#EAE6DF] space-y-4">
          {visits.map((v) => (
            <div key={v.id} className="relative" data-testid={`visit-${v.id}`}>
              <div className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full bg-[#4A5D4E] border-2 border-white" />
              <div className="text-xs text-[#6E7772]">
                {formatDate(v.created_at || v.visit_date)} · {v.employee_name || "—"}
                {v.auto && <span className="ml-1 text-[10px] bg-[#EAE6DF] text-[#2A2F2C] px-1.5 py-0.5 rounded">auto</span>}
              </div>
              <div className="font-medium text-[#2A2F2C] mt-0.5">{v.reason}</div>
              {v.notes && <div className="text-sm text-[#6E7772]">{v.notes}</div>}
              {!v.auto && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 text-[#C07F67]"
                  onClick={() => remove(v)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}


function ContactsSection({ patientId, patient, contacts, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    channel: "call",
    result: "no_answer",
    notes: "",
    contacted_at: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm for datetime-local
  });

  const resetForm = () => setForm({
    channel: "call",
    result: "no_answer",
    notes: "",
    contacted_at: new Date().toISOString().slice(0, 16),
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        contacted_at: form.contacted_at ? new Date(form.contacted_at).toISOString() : null,
      };
      await api.post(`/patients/${patientId}/contacts`, payload);
      toast.success("Contacto registrado");
      resetForm();
      setOpen(false);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cid) => {
    if (!confirm("¿Eliminar este contacto del historial?")) return;
    try {
      await api.delete(`/patients/${patientId}/contacts/${cid}`);
      toast.success("Contacto eliminado");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const channelLabel = (c) => (CONTACT_CHANNELS.find((x) => x.value === c) || {}).label || c;
  const resultLabel = (r) => (CONTACT_RESULTS.find((x) => x.value === r) || {}).label || r;
  const resultColor = (r) => {
    switch (r) {
      case "done":
      case "scheduled":
        return "bg-[#D1E0D7] text-[#2A2F2C]";
      case "no_answer":
        return "bg-[#F1E5C0] text-[#2A2F2C]";
      case "callback":
        return "bg-[#F3E5D8] text-[#2A2F2C]";
      case "not_interested":
        return "bg-[#E8D1D1] text-[#2A2F2C]";
      default:
        return "bg-[#EAE6DF] text-[#2A2F2C]";
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#6E7772]">
          Historial de llamadas, mensajes y contactos con el paciente.
        </p>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-contact-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Phone className="h-4 w-4 mr-2" strokeWidth={1.5} /> Registrar contacto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nuevo contacto</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Canal</Label>
                  <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                    <SelectTrigger data-testid="contact-channel" className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_CHANNELS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Resultado</Label>
                  <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                    <SelectTrigger data-testid="contact-result" className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_RESULTS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Fecha y hora</Label>
                <Input
                  type="datetime-local"
                  data-testid="contact-datetime"
                  value={form.contacted_at}
                  onChange={(e) => setForm({ ...form, contacted_at: e.target.value })}
                />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  data-testid="contact-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-contact-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {contacts.length === 0 ? (
        <Card className="border-[#E5E3DB] shadow-none bg-white">
          <CardContent className="p-12 text-center text-[#6E7772]">
            Sin contactos registrados. Registra cada llamada, WhatsApp o email
            para mantener un historial completo.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <Card key={c.id} className="border-[#E5E3DB] shadow-none bg-white" data-testid={`contact-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-[#2A2F2C]">
                        {formatDate(c.contacted_at)} · {(c.contacted_at || "").slice(11, 16)}
                      </span>
                      <Badge className="bg-[#EAE6DF] text-[#2A2F2C]">{channelLabel(c.channel)}</Badge>
                      <Badge className={resultColor(c.result)}>{resultLabel(c.result)}</Badge>
                      <span className="text-xs text-[#6E7772]">por {c.created_by_name}</span>
                    </div>
                    {c.notes && <div className="text-sm text-[#4B5563] mt-1 whitespace-pre-wrap">{c.notes}</div>}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-[#9CA3AF] hover:text-[#C04848]"
                    onClick={() => remove(c.id)}
                    data-testid={`contact-delete-${c.id}`}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}


// ============================================================================
// Pruebas clínicas (Noah + manuales) + Generador de informe PDF
// ============================================================================

const TEST_TYPE_OPTIONS = [
  { value: "audiogram", label: "Audiograma tonal" },
  { value: "speech_audiometry", label: "Logoaudiometría" },
  { value: "tympanometry", label: "Timpanometría" },
  { value: "acoustic_reflex", label: "Reflejos acústicos" },
  { value: "rem", label: "Medición de oído real (REM)" },
  { value: "in_situ", label: "Medición in-situ" },
  { value: "fitting", label: "Sesión de adaptación" },
  { value: "counseling", label: "Asesoramiento" },
  { value: "otoscopia", label: "Otoscopia" },
  { value: "other", label: "Otra prueba" },
];

const TEST_TYPE_LABEL = Object.fromEntries(TEST_TYPE_OPTIONS.map((o) => [o.value, o.label]));

function ClinicalTestsSection({ patient, tests, audiologists, user, onChange }) {
  const [filterType, setFilterType] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [openReport, setOpenReport] = useState(false);
  const [openCompare, setOpenCompare] = useState(false);
  const canWrite = ["admin", "gerente", "audioprotesista"].includes(user?.role);

  const audiograms = tests.filter((t) => t.test_type === "audiogram");

  const filtered = filterType === "all" ? tests : tests.filter((t) => t.test_type === filterType);

  const syncNoah = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/clinical-tests/sync-noah`);
      toast.success(`Noah sincronizado: ${data.inserted} nuevas, ${data.skipped} ya existentes`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const removeTest = async (tid) => {
    if (!window.confirm("¿Eliminar esta prueba?")) return;
    try {
      await api.delete(`/clinical-tests/${tid}`);
      toast.success("Prueba eliminada");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="clinical-tests-section">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div>
          <h3 className="font-heading text-lg">Pruebas clínicas</h3>
          <p className="text-xs text-[#6E7772]">
            Todas las pruebas sincronizadas desde Noah y las pruebas manuales (otoscopia, anotaciones, etc.).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-52" data-testid="clinical-filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las pruebas</SelectItem>
              {TEST_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canWrite && (
            <Button
              variant="outline"
              onClick={syncNoah}
              disabled={syncing}
              data-testid="clinical-sync-noah"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando…" : "Sincronizar Noah"}
            </Button>
          )}
          {canWrite && (
            <Button
              variant="outline"
              onClick={() => setOpenManual(true)}
              data-testid="clinical-new-manual"
            >
              <Plus className="h-4 w-4 mr-1" /> Nueva prueba
            </Button>
          )}
          <Button
            onClick={() => setOpenCompare(true)}
            variant="outline"
            data-testid="clinical-open-compare"
            disabled={audiograms.length < 2}
            title={audiograms.length < 2 ? "Se necesitan al menos 2 audiogramas" : "Comparar audiogramas"}
          >
            <Ear className="h-4 w-4 mr-1" /> Comparar
          </Button>
          <Button
            onClick={() => setOpenReport(true)}
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            data-testid="clinical-open-report"
            disabled={tests.length === 0}
          >
            <FileText className="h-4 w-4 mr-1" /> Generar informe
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#6E7772] border border-dashed border-[#E5E3DB] rounded-lg">
          <Stethoscope className="h-10 w-10 mx-auto mb-3 opacity-60" strokeWidth={1.2} />
          <div className="text-sm">
            {tests.length === 0
              ? "No hay pruebas todavía. Sincroniza con Noah o añade una manual."
              : "No hay pruebas de este tipo."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="clinical-cards">
          {filtered.map((t) => (
            <ClinicalTestCard
              key={t.id}
              test={t}
              canWrite={canWrite}
              onRemove={() => removeTest(t.id)}
              onUpdated={onChange}
            />
          ))}
        </div>
      )}

      {openManual && (
        <ManualClinicalTestDialog
          patientId={patient.id}
          onClose={() => setOpenManual(false)}
          onSaved={() => {
            setOpenManual(false);
            onChange();
          }}
        />
      )}

      {openReport && (
        <ClinicalReportDialog
          patient={patient}
          tests={tests}
          audiologists={audiologists}
          user={user}
          onClose={() => setOpenReport(false)}
        />
      )}

      {openCompare && (
        <CompareAudiogramsDialog
          audiograms={audiograms}
          onClose={() => setOpenCompare(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// SVG mini-charts (inline en cada tarjeta)
// ============================================================================
const AUDIO_FREQS = [125, 250, 500, 750, 1000, 1500, 2000, 3000, 4000, 6000, 8000];
const STD_AUDIO_FREQS = [250, 500, 1000, 2000, 4000, 8000];

// Series del audiograma con su símbolo, color, y si dibuja línea de unión
// Convención clínica española (no estricta ASHA pero suficientemente reconocible):
const AUDIO_SERIES = [
  { key: "ac_right",         label: "VA Derecho",            color: "#C73E1D", marker: "O",        line: "solid"  },
  { key: "ac_left",          label: "VA Izquierdo",          color: "#1F4E79", marker: "X",        line: "solid"  },
  { key: "ac_right_masked",  label: "VA Derecho enmasc.",    color: "#C73E1D", marker: "tri-up",   line: "solid"  },
  { key: "ac_left_masked",   label: "VA Izquierdo enmasc.",  color: "#1F4E79", marker: "square",   line: "solid"  },
  { key: "bc_right",         label: "VO Derecho",            color: "#C73E1D", marker: "lt",       line: "dashed" },
  { key: "bc_left",          label: "VO Izquierdo",          color: "#1F4E79", marker: "gt",       line: "dashed" },
  { key: "bc_right_masked",  label: "VO Derecho enmasc.",    color: "#C73E1D", marker: "bracket-l",line: "dashed" },
  { key: "bc_left_masked",   label: "VO Izquierdo enmasc.",  color: "#1F4E79", marker: "bracket-r",line: "dashed" },
  { key: "ucl_right",        label: "UCL Derecho",           color: "#C73E1D", marker: "U",        line: "none"   },
  { key: "ucl_left",         label: "UCL Izquierdo",         color: "#1F4E79", marker: "U",        line: "none"   },
];
const AUDIO_SERIES_BY_KEY = Object.fromEntries(AUDIO_SERIES.map((s) => [s.key, s]));

function drawMarker(marker, x, y, color, noResp = false) {
  const r = 3.5;
  const els = [];
  switch (marker) {
    case "O":
      els.push(<circle key="m" cx={x} cy={y} r={r} fill="white" stroke={color} strokeWidth={1.4} />);
      break;
    case "X":
      els.push(<line key="m1" x1={x - r} x2={x + r} y1={y - r} y2={y + r} stroke={color} strokeWidth={1.4} />);
      els.push(<line key="m2" x1={x - r} x2={x + r} y1={y + r} y2={y - r} stroke={color} strokeWidth={1.4} />);
      break;
    case "tri-up":
      els.push(<polygon key="m" points={`${x},${y - r} ${x - r},${y + r} ${x + r},${y + r}`} fill="white" stroke={color} strokeWidth={1.4} />);
      break;
    case "square":
      els.push(<rect key="m" x={x - r} y={y - r} width={r * 2} height={r * 2} fill="white" stroke={color} strokeWidth={1.4} />);
      break;
    case "lt":
      els.push(<polyline key="m" fill="none" stroke={color} strokeWidth={1.4} points={`${x + r},${y - r} ${x - r},${y} ${x + r},${y + r}`} />);
      break;
    case "gt":
      els.push(<polyline key="m" fill="none" stroke={color} strokeWidth={1.4} points={`${x - r},${y - r} ${x + r},${y} ${x - r},${y + r}`} />);
      break;
    case "bracket-l":
      els.push(<polyline key="m" fill="none" stroke={color} strokeWidth={1.4} points={`${x + r},${y - r} ${x - r},${y - r} ${x - r},${y + r} ${x + r},${y + r}`} />);
      break;
    case "bracket-r":
      els.push(<polyline key="m" fill="none" stroke={color} strokeWidth={1.4} points={`${x - r},${y - r} ${x + r},${y - r} ${x + r},${y + r} ${x - r},${y + r}`} />);
      break;
    case "U":
      els.push(<text key="m" x={x} y={y + 3.5} textAnchor="middle" fontSize={10} fontWeight={700} fill={color}>U</text>);
      break;
    default:
      els.push(<circle key="m" cx={x} cy={y} r={2} fill={color} />);
  }
  if (noResp) {
    els.push(<line key="nr1" x1={x} x2={x} y1={y + r + 1} y2={y + r + 9} stroke={color} strokeWidth={1.2} />);
    els.push(<polyline key="nr2" fill="none" stroke={color} strokeWidth={1.2} points={`${x - 2},${y + r + 6} ${x},${y + r + 9} ${x + 2},${y + r + 6}`} />);
  }
  return els;
}

function AudiogramSvg({ parsed, height = 200, overlays = null }) {
  // overlays = [{ series: { ac_right:[...], ac_left:[...] }, color: "#..", label:"..." }, ...]
  const W = 360, H = height;
  const padL = 30, padR = 12, padT = 10, padB = 24;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const dbMin = -10, dbMax = 120;
  const logMin = Math.log10(AUDIO_FREQS[0]);
  const logMax = Math.log10(AUDIO_FREQS[AUDIO_FREQS.length - 1]);
  const fx = (f) => padL + (plotW * (Math.log10(f) - logMin)) / (logMax - logMin);
  const fy = (db) => padT + plotH * (1 - (db - dbMin) / (dbMax - dbMin));

  const grid = [];
  for (let db = dbMin; db <= dbMax; db += 10) {
    grid.push(<line key={`h${db}`} x1={padL} x2={padL + plotW} y1={fy(db)} y2={fy(db)} stroke="#E5E3DB" strokeWidth={0.5} />);
    if (db % 20 === 0) grid.push(<text key={`th${db}`} x={padL - 3} y={fy(db) + 3} fontSize={8} fill="#6E7772" textAnchor="end">{db}</text>);
  }
  AUDIO_FREQS.forEach((f) => {
    grid.push(<line key={`v${f}`} x1={fx(f)} x2={fx(f)} y1={padT} y2={padT + plotH} stroke="#E5E3DB" strokeWidth={0.5} />);
    grid.push(<text key={`tv${f}`} x={fx(f)} y={H - 6} fontSize={8} fill="#6E7772" textAnchor="middle">{f >= 1000 ? `${f / 1000}k` : f}</text>);
  });

  const renderSeries = (data, sdef, overrideColor = null) => {
    if (!data?.length) return null;
    const color = overrideColor || sdef.color;
    const pts = [...data]
      .filter((p) => p.threshold != null && p.freq)
      .sort((a, b) => a.freq - b.freq);
    if (pts.length === 0) return null;
    const linePts = pts.filter((p) => !p.no_response).map((p) => `${fx(p.freq)},${fy(p.threshold)}`).join(" ");
    return (
      <g>
        {sdef.line !== "none" && linePts && (
          <polyline
            fill="none"
            stroke={color}
            strokeWidth={1.4}
            strokeDasharray={sdef.line === "dashed" ? "4 3" : ""}
            points={linePts}
          />
        )}
        {pts.map((p, idx) => (
          <g key={idx}>{drawMarker(sdef.marker, fx(p.freq), fy(p.threshold), color, !!p.no_response)}</g>
        ))}
      </g>
    );
  };

  // Datos legacy (left_ear/right_ear) → tratar como AC
  const dataAcRight = parsed?.ac_right || parsed?.right_ear || [];
  const dataAcLeft = parsed?.ac_left || parsed?.left_ear || [];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="bg-white rounded">
      <rect x={padL} y={padT} width={plotW} height={plotH} fill="white" stroke="#2A2F2C" strokeWidth={0.6} />
      {grid}
      {renderSeries(dataAcRight, AUDIO_SERIES_BY_KEY.ac_right)}
      {renderSeries(dataAcLeft, AUDIO_SERIES_BY_KEY.ac_left)}
      {renderSeries(parsed?.ac_right_masked, AUDIO_SERIES_BY_KEY.ac_right_masked)}
      {renderSeries(parsed?.ac_left_masked, AUDIO_SERIES_BY_KEY.ac_left_masked)}
      {renderSeries(parsed?.bc_right, AUDIO_SERIES_BY_KEY.bc_right)}
      {renderSeries(parsed?.bc_left, AUDIO_SERIES_BY_KEY.bc_left)}
      {renderSeries(parsed?.bc_right_masked, AUDIO_SERIES_BY_KEY.bc_right_masked)}
      {renderSeries(parsed?.bc_left_masked, AUDIO_SERIES_BY_KEY.bc_left_masked)}
      {renderSeries(parsed?.ucl_right, AUDIO_SERIES_BY_KEY.ucl_right)}
      {renderSeries(parsed?.ucl_left, AUDIO_SERIES_BY_KEY.ucl_left)}

      {(overlays || []).map((ov, i) => (
        <g key={i} opacity="0.7">
          {Object.entries(ov.series || {}).map(([k, v]) => {
            const sd = AUDIO_SERIES_BY_KEY[k];
            if (!sd) return null;
            return <g key={k}>{renderSeries(v, sd, ov.color)}</g>;
          })}
        </g>
      ))}
    </svg>
  );
}

function SpeechCurveSvg({ parsed, height = 140 }) {
  const W = 320, H = height;
  const padL = 28, padR = 8, padT = 8, padB = 20;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const dbMin = 0, dbMax = 110;
  const fx = (db) => padL + (plotW * (db - dbMin)) / (dbMax - dbMin);
  const fy = (pct) => padT + plotH * (1 - pct / 100);

  const grid = [];
  for (let db = 0; db <= 110; db += 10) {
    grid.push(<line key={`v${db}`} x1={fx(db)} x2={fx(db)} y1={padT} y2={padT + plotH} stroke="#E5E3DB" strokeWidth={0.4} />);
    if (db % 20 === 0) grid.push(<text key={`tv${db}`} x={fx(db)} y={H - 4} fontSize={8} fill="#6E7772" textAnchor="middle">{db}</text>);
  }
  for (let p = 0; p <= 100; p += 20) {
    grid.push(<line key={`h${p}`} x1={padL} x2={padL + plotW} y1={fy(p)} y2={fy(p)} stroke="#E5E3DB" strokeWidth={0.4} />);
    grid.push(<text key={`th${p}`} x={padL - 3} y={fy(p) + 3} fontSize={8} fill="#6E7772" textAnchor="end">{p}%</text>);
  }

  const renderEar = (points, color) => {
    if (!points?.length) return null;
    const pts = [...points].filter((p) => p.db != null && p.score != null).sort((a, b) => a.db - b.db);
    if (pts.length === 0) return null;
    const poly = pts.map((p) => `${fx(p.db)},${fy(p.score)}`).join(" ");
    return (
      <g>
        <polyline fill="none" stroke={color} strokeWidth={1.4} points={poly} />
        {pts.map((p, idx) => <circle key={idx} cx={fx(p.db)} cy={fy(p.score)} r={2.5} fill={color} />)}
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="bg-white rounded">
      <rect x={padL} y={padT} width={plotW} height={plotH} fill="white" stroke="#2A2F2C" strokeWidth={0.6} />
      {grid}
      {renderEar(parsed?.right_ear, "#C73E1D")}
      {renderEar(parsed?.left_ear, "#1F4E79")}
    </svg>
  );
}

function AudiogramCardContent({ parsed }) {
  const has = (k) => Array.isArray(parsed?.[k]) && parsed[k].length > 0;
  // Legacy: si solo hay left_ear/right_ear (de Noah antiguos), AudiogramSvg ya los trata como AC
  const anyData =
    has("ac_right") || has("ac_left") || has("ac_right_masked") || has("ac_left_masked") ||
    has("bc_right") || has("bc_left") || has("bc_right_masked") || has("bc_left_masked") ||
    has("ucl_right") || has("ucl_left") ||
    has("right_ear") || has("left_ear");
  if (!anyData) {
    return <div className="text-xs text-[#6E7772] py-3 text-center">Sin datos numéricos.</div>;
  }
  const presentKeys = AUDIO_SERIES.filter((s) => has(s.key));
  // Si no hay series nuevas, mostramos leyenda básica AC
  const legend = presentKeys.length > 0 ? presentKeys : [
    AUDIO_SERIES_BY_KEY.ac_right,
    AUDIO_SERIES_BY_KEY.ac_left,
  ];
  return (
    <div>
      <AudiogramSvg parsed={parsed} />
      <div className="flex gap-x-3 gap-y-1 text-[10px] text-[#6E7772] justify-center mt-1 flex-wrap">
        {legend.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <svg width="16" height="14" viewBox="0 0 16 14">{drawMarker(s.marker, 8, 7, s.color)}</svg>
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function OtoscopyImage({ testId, attachmentPath }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    setSrc(null);
    api.get(`/clinical-tests/${testId}/attachment`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [testId, attachmentPath]);
  if (!src) {
    return (
      <div className="rounded border border-dashed border-[#E5E3DB] bg-[#F7F6F3] h-40 flex items-center justify-center text-xs text-[#6E7772]">
        Cargando imagen…
      </div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img
        src={src}
        alt="Imagen de la prueba"
        className="rounded border border-[#E5E3DB] max-h-56 w-full object-contain bg-[#F7F6F3]"
        data-testid={`otoscopy-img-${testId}`}
      />
    </a>
  );
}

function ClinicalTestCard({ test, canWrite, onRemove, onUpdated }) {
  const parsed = test.parsed || {};
  const isImage = (test.attachment_content_type || "").startsWith("image/");
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/clinical-tests/${test.id}/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagen añadida a la prueba");
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none" data-testid={`clinical-card-${test.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-medium text-[15px]">{TEST_TYPE_LABEL[test.test_type] || test.test_type}</div>
            <div className="text-xs text-[#6E7772]">{test.date}{test.test_label && test.test_label !== test.test_type ? ` · ${test.test_label}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            {test.source === "noah" ? (
              <Badge className="bg-[#D1E0D7] text-[#2A2F2C] text-[10px]">Noah</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">Manual</Badge>
            )}
            {canWrite && (
              <label
                className="cursor-pointer text-[#9CA3AF] hover:text-[#4A5D4E] h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-[#F7F6F3]"
                title={test.attachment_path ? "Cambiar imagen de la prueba" : "Añadir imagen a la prueba"}
              >
                {uploading ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ImagePlus className="h-3.5 w-3.5" />
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={uploading}
                  onChange={uploadImage}
                  data-testid={`test-add-image-${test.id}`}
                />
              </label>
            )}
            {canWrite && (
              <Button size="icon" variant="ghost" className="text-[#9CA3AF] hover:text-[#C04848] h-7 w-7" onClick={onRemove}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {test.test_type === "audiogram" ? (
          <AudiogramCardContent parsed={parsed} />
        ) : null}

        {test.test_type === "speech_audiometry" && (parsed.left_ear?.length || parsed.right_ear?.length) ? (
          <SpeechCurveSvg parsed={parsed} />
        ) : null}

        {test.test_type === "tympanometry" && (parsed.left_ear || parsed.right_ear) ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-[#E5E3DB]">
                <TableHead className="text-xs">Oído</TableHead>
                <TableHead className="text-xs">Pico (daPa)</TableHead>
                <TableHead className="text-xs">Complianza</TableHead>
                <TableHead className="text-xs">Volumen</TableHead>
                <TableHead className="text-xs">Tipo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {["right_ear", "left_ear"].map((k) => {
                const v = parsed[k] || {};
                return (
                  <TableRow key={k} className="border-[#E5E3DB]">
                    <TableCell className="text-sm">{k === "right_ear" ? "Derecho" : "Izquierdo"}</TableCell>
                    <TableCell className="text-sm">{v.peak_pressure_daPa ?? "—"}</TableCell>
                    <TableCell className="text-sm">{v.compliance_ml ?? "—"}</TableCell>
                    <TableCell className="text-sm">{v.volume_ml ?? "—"}</TableCell>
                    <TableCell className="text-sm">{v.type ?? "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}

        {isImage && test.attachment_path ? (
          <OtoscopyImage testId={test.id} attachmentPath={test.attachment_path} />
        ) : null}

        {test.notes && (
          <div className="text-sm text-[#2A2F2C] whitespace-pre-wrap bg-[#FAF9F6] rounded p-2 border border-[#E5E3DB]">
            {test.notes}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Componentes auxiliares de entrada manual (audiograma completo + logoaudio + reflejos)
// ----------------------------------------------------------------------------
const AUDIO_INPUT_SERIES = [
  { key: "ac_right",         label: "VA Derecho",            color: "#C73E1D" },
  { key: "ac_left",          label: "VA Izquierdo",          color: "#1F4E79" },
  { key: "ac_right_masked",  label: "VA Dcho enmascarado",   color: "#C73E1D" },
  { key: "ac_left_masked",   label: "VA Izdo enmascarado",   color: "#1F4E79" },
  { key: "bc_right",         label: "VO Derecho",            color: "#C73E1D" },
  { key: "bc_left",          label: "VO Izquierdo",          color: "#1F4E79" },
  { key: "bc_right_masked",  label: "VO Dcho enmascarado",   color: "#C73E1D" },
  { key: "bc_left_masked",   label: "VO Izdo enmascarado",   color: "#1F4E79" },
  { key: "ucl_right",        label: "Umbral disconfort OD",  color: "#C73E1D" },
  { key: "ucl_left",         label: "Umbral disconfort OI",  color: "#1F4E79" },
];

function emptyAudioSeries() {
  const obj = {};
  AUDIO_INPUT_SERIES.forEach((s) => {
    obj[s.key] = Object.fromEntries(STD_AUDIO_FREQS.map((f) => [f, { v: "", nr: false }]));
  });
  return obj;
}

function AudiogramManualForm({ data, setData, enabled, setEnabled }) {
  const toggleEnabled = (k) => setEnabled({ ...enabled, [k]: !enabled[k] });
  return (
    <div className="space-y-3 border border-[#E5E3DB] rounded-lg p-3 bg-[#FAF9F6]">
      <div>
        <div className="text-sm font-medium">Audiograma — series</div>
        <p className="text-xs text-[#6E7772]">Marca las series medidas. Deja en blanco las frecuencias no testadas. <strong>NR</strong> = No respuesta.</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {AUDIO_INPUT_SERIES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => toggleEnabled(s.key)}
            className={`text-[11px] px-2 py-1 rounded-full border transition ${
              enabled[s.key]
                ? "border-transparent text-white"
                : "border-[#E5E3DB] bg-white text-[#6E7772]"
            }`}
            style={enabled[s.key] ? { backgroundColor: s.color } : {}}
            data-testid={`audio-toggle-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {AUDIO_INPUT_SERIES.filter((s) => enabled[s.key]).map((s) => (
        <div key={s.key} className="border border-[#E5E3DB] rounded-md p-2 bg-white">
          <div className="text-xs font-medium mb-1.5" style={{ color: s.color }}>{s.label} (dB HL)</div>
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${STD_AUDIO_FREQS.length}, minmax(0, 1fr))` }}>
            {STD_AUDIO_FREQS.map((f) => (
              <div key={f} className="text-center">
                <div className="text-[10px] text-[#6E7772] mb-0.5">{f >= 1000 ? `${f / 1000}k` : f}</div>
                <Input
                  type="number"
                  step="5"
                  min={-10}
                  max={130}
                  placeholder="—"
                  className="text-center text-sm h-7 px-1"
                  value={data[s.key]?.[f]?.v ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setData({
                      ...data,
                      [s.key]: { ...data[s.key], [f]: { ...(data[s.key]?.[f] || {}), v } },
                    });
                  }}
                  data-testid={`audio-input-${s.key}-${f}`}
                />
                <label className="flex items-center justify-center gap-0.5 text-[9px] text-[#6E7772] mt-0.5">
                  <input
                    type="checkbox"
                    className="h-2.5 w-2.5"
                    checked={!!data[s.key]?.[f]?.nr}
                    onChange={(e) => setData({
                      ...data,
                      [s.key]: { ...data[s.key], [f]: { ...(data[s.key]?.[f] || {}), nr: e.target.checked } },
                    })}
                  />
                  NR
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SpeechManualForm({ data, setData }) {
  const setEar = (ear, field, v) => setData({ ...data, [ear]: { ...data[ear], [field]: v } });
  return (
    <div className="space-y-3 border border-[#E5E3DB] rounded-lg p-3 bg-[#FAF9F6]">
      <div className="text-sm font-medium">Logoaudiometría</div>
      <p className="text-xs text-[#6E7772]">SRT = umbral de recepción del habla · MCL = nivel más cómodo · UCL = nivel intolerable · % discriminación al MCL.</p>
      {[
        { key: "right", side: "Oído derecho", color: "#C73E1D" },
        { key: "left",  side: "Oído izquierdo", color: "#1F4E79" },
      ].map((row) => (
        <div key={row.key} className="border border-[#E5E3DB] rounded-md p-2 bg-white">
          <div className="text-xs font-medium mb-1.5" style={{ color: row.color }}>{row.side}</div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <div className="text-[10px] text-[#6E7772]">SRT (dB)</div>
              <Input type="number" className="h-8 text-sm" value={data[row.key]?.srt ?? ""} onChange={(e) => setEar(row.key, "srt", e.target.value)} data-testid={`speech-${row.key}-srt`} />
            </div>
            <div>
              <div className="text-[10px] text-[#6E7772]">MCL (dB)</div>
              <Input type="number" className="h-8 text-sm" value={data[row.key]?.mcl ?? ""} onChange={(e) => setEar(row.key, "mcl", e.target.value)} />
            </div>
            <div>
              <div className="text-[10px] text-[#6E7772]">UCL (dB)</div>
              <Input type="number" className="h-8 text-sm" value={data[row.key]?.ucl ?? ""} onChange={(e) => setEar(row.key, "ucl", e.target.value)} />
            </div>
            <div>
              <div className="text-[10px] text-[#6E7772]">Discrim. (%)</div>
              <Input type="number" min={0} max={100} className="h-8 text-sm" value={data[row.key]?.discrim ?? ""} onChange={(e) => setEar(row.key, "discrim", e.target.value)} />
            </div>
          </div>
          <div className="mt-2">
            <div className="text-[10px] text-[#6E7772]">Lista usada / observaciones</div>
            <Input className="h-8 text-sm" value={data[row.key]?.list ?? ""} onChange={(e) => setEar(row.key, "list", e.target.value)} />
          </div>
        </div>
      ))}
    </div>
  );
}

const REFLEX_FREQS = [500, 1000, 2000, 4000];

function ReflexManualForm({ data, setData }) {
  return (
    <div className="space-y-3 border border-[#E5E3DB] rounded-lg p-3 bg-[#FAF9F6]">
      <div className="text-sm font-medium">Reflejos acústicos (umbrales en dB HL)</div>
      <p className="text-xs text-[#6E7772]">Marca <strong>A</strong> si está ausente. Sonda en el oído indicado, estímulo ipsi/contralateral.</p>
      {[
        { key: "ipsi_right",   label: "Ipsi Dcho (sonda OD · estímulo OD)",     color: "#C73E1D" },
        { key: "ipsi_left",    label: "Ipsi Izdo (sonda OI · estímulo OI)",     color: "#1F4E79" },
        { key: "contra_right", label: "Contra Dcho (sonda OD · estímulo OI)",  color: "#C73E1D" },
        { key: "contra_left",  label: "Contra Izdo (sonda OI · estímulo OD)",  color: "#1F4E79" },
      ].map((row) => (
        <div key={row.key} className="border border-[#E5E3DB] rounded-md p-2 bg-white">
          <div className="text-xs font-medium mb-1" style={{ color: row.color }}>{row.label}</div>
          <div className="grid grid-cols-4 gap-2">
            {REFLEX_FREQS.map((f) => (
              <div key={f}>
                <div className="text-[10px] text-[#6E7772]">{f} Hz</div>
                <Input
                  className="h-8 text-sm text-center"
                  placeholder="dB / A"
                  value={data[row.key]?.[f] ?? ""}
                  onChange={(e) => setData({ ...data, [row.key]: { ...data[row.key], [f]: e.target.value } })}
                  data-testid={`reflex-${row.key}-${f}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ManualClinicalTestDialog({ patientId, onClose, onSaved }) {
  const [form, setForm] = useState({
    test_type: "otoscopia",
    test_label: "",
    date: new Date().toISOString().slice(0, 10),
    notes: "",
    ear: "binaural",
  });
  // Audiograma: estructura por serie con dB + NR
  const [audioData, setAudioData] = useState(emptyAudioSeries());
  const [audioEnabled, setAudioEnabled] = useState({
    ac_right: true, ac_left: true,
    ac_right_masked: false, ac_left_masked: false,
    bc_right: false, bc_left: false,
    bc_right_masked: false, bc_left_masked: false,
    ucl_right: false, ucl_left: false,
  });
  // Logoaudiometría
  const [speech, setSpeech] = useState({ right: {}, left: {} });
  // Reflejos
  const [reflex, setReflex] = useState({ ipsi_right: {}, ipsi_left: {}, contra_right: {}, contra_left: {} });
  // Timpanometría
  const [tymp, setTymp] = useState({ rPeak: "", rComp: "", rVol: "", rType: "", lPeak: "", lComp: "", lVol: "", lType: "" });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const buildParsed = () => {
    if (form.test_type === "audiogram") {
      const out = {};
      AUDIO_INPUT_SERIES.forEach((s) => {
        if (!audioEnabled[s.key]) return;
        const pts = [];
        STD_AUDIO_FREQS.forEach((f) => {
          const cell = audioData[s.key]?.[f];
          if (!cell) return;
          if (cell.v === "" && !cell.nr) return;
          pts.push({
            freq: f,
            threshold: cell.v === "" ? null : parseFloat(cell.v),
            ...(cell.nr ? { no_response: true } : {}),
          });
        });
        if (pts.length > 0) out[s.key] = pts;
      });
      return out;
    }
    if (form.test_type === "speech_audiometry") {
      const norm = (e) => ({
        srt: e.srt !== "" && e.srt != null ? parseFloat(e.srt) : null,
        mcl: e.mcl !== "" && e.mcl != null ? parseFloat(e.mcl) : null,
        ucl: e.ucl !== "" && e.ucl != null ? parseFloat(e.ucl) : null,
        discrim: e.discrim !== "" && e.discrim != null ? parseFloat(e.discrim) : null,
        list: e.list || null,
      });
      return { right: norm(speech.right || {}), left: norm(speech.left || {}) };
    }
    if (form.test_type === "acoustic_reflex") {
      return { ...reflex };
    }
    if (form.test_type === "tympanometry") {
      return {
        right_ear: {
          peak_pressure_daPa: tymp.rPeak ? parseFloat(tymp.rPeak) : null,
          compliance_ml: tymp.rComp ? parseFloat(tymp.rComp) : null,
          volume_ml: tymp.rVol ? parseFloat(tymp.rVol) : null,
          type: tymp.rType || null,
        },
        left_ear: {
          peak_pressure_daPa: tymp.lPeak ? parseFloat(tymp.lPeak) : null,
          compliance_ml: tymp.lComp ? parseFloat(tymp.lComp) : null,
          volume_ml: tymp.lVol ? parseFloat(tymp.lVol) : null,
          type: tymp.lType || null,
        },
      };
    }
    return {};
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, parsed: buildParsed() };
      const { data: created } = await api.post(`/patients/${patientId}/clinical-tests`, payload);
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await api.post(`/clinical-tests/${created.id}/attachment`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Prueba registrada");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="manual-clinical-dialog"
      >
        <DialogHeader>
          <DialogTitle>Nueva prueba manual</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <Label>Tipo</Label>
              <Select value={form.test_type} onValueChange={(v) => setForm({ ...form, test_type: v })}>
                <SelectTrigger data-testid="manual-test-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TEST_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Oído</Label>
              <Select value={form.ear} onValueChange={(v) => setForm({ ...form, ear: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="binaural">Ambos</SelectItem>
                  <SelectItem value="right">Derecho</SelectItem>
                  <SelectItem value="left">Izquierdo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Etiqueta (opcional)</Label>
            <Input
              value={form.test_label}
              onChange={(e) => setForm({ ...form, test_label: e.target.value })}
            />
          </div>

          {form.test_type === "audiogram" && (
            <AudiogramManualForm data={audioData} setData={setAudioData} enabled={audioEnabled} setEnabled={setAudioEnabled} />
          )}

          {form.test_type === "speech_audiometry" && (
            <SpeechManualForm data={speech} setData={setSpeech} />
          )}

          {form.test_type === "acoustic_reflex" && (
            <ReflexManualForm data={reflex} setData={setReflex} />
          )}

          {form.test_type === "tympanometry" && (
            <div className="space-y-3 border border-[#E5E3DB] rounded-lg p-3 bg-[#FAF9F6]">
              <div className="text-sm font-medium">Datos timpanometría</div>
              {[
                { side: "Derecho", color: "#C73E1D", keys: ["rPeak", "rComp", "rVol", "rType"] },
                { side: "Izquierdo", color: "#1F4E79", keys: ["lPeak", "lComp", "lVol", "lType"] },
              ].map((row) => (
                <div key={row.side} className="grid grid-cols-5 gap-2 items-center">
                  <div className="text-xs font-medium" style={{ color: row.color }}>{row.side}</div>
                  <Input type="number" step="0.1" placeholder="Pico (daPa)" value={tymp[row.keys[0]]} onChange={(e) => setTymp({ ...tymp, [row.keys[0]]: e.target.value })} className="h-9 text-sm" />
                  <Input type="number" step="0.1" placeholder="Complianza (ml)" value={tymp[row.keys[1]]} onChange={(e) => setTymp({ ...tymp, [row.keys[1]]: e.target.value })} className="h-9 text-sm" />
                  <Input type="number" step="0.1" placeholder="Volumen (ml)" value={tymp[row.keys[2]]} onChange={(e) => setTymp({ ...tymp, [row.keys[2]]: e.target.value })} className="h-9 text-sm" />
                  <Select value={tymp[row.keys[3]]} onValueChange={(v) => setTymp({ ...tymp, [row.keys[3]]: v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Tipo A (normal)</SelectItem>
                      <SelectItem value="As">Tipo As (rígido)</SelectItem>
                      <SelectItem value="Ad">Tipo Ad (flácido)</SelectItem>
                      <SelectItem value="B">Tipo B (plano)</SelectItem>
                      <SelectItem value="C">Tipo C (presión neg.)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 border border-[#E5E3DB] rounded-lg p-3 bg-[#FAF9F6]">
            <Label>Imagen de la prueba (opcional) — se incluirá en el informe</Label>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              data-testid="otoscopy-file"
            />
            {imageFile && (
              <div className="text-xs text-[#6E7772]">Seleccionado: {imageFile.name}</div>
            )}
          </div>

          <div>
            <Label>Observaciones</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={form.test_type === "otoscopia" ? 3 : 4}
              placeholder="Observaciones, hallazgos, condiciones del conducto…"
              data-testid="manual-notes"
            />
          </div>
        </div>

        <DialogFooter className="border-t border-[#E5E3DB] pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            data-testid="manual-save"
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClinicalReportDialog({ patient, tests, audiologists, user, onClose }) {
  const [selected, setSelected] = useState(new Set(tests.map((t) => t.id)));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [conclusions, setConclusions] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [audiologistId, setAudiologistId] = useState(user?.id || "");
  const [generating, setGenerating] = useState(false);
  const [extraImages, setExtraImages] = useState([]); // [{id, name, caption, dataUrl}]

  const downscaleImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("No se pudo leer la imagen"));
      };
      img.src = url;
    });

  const addImages = async (files) => {
    const list = Array.from(files || []);
    const added = [];
    for (const f of list) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} no es una imagen`);
        continue;
      }
      if (extraImages.length + added.length >= 12) {
        toast.error("Máximo 12 imágenes por informe");
        break;
      }
      try {
        const dataUrl = await downscaleImage(f);
        added.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: f.name,
          caption: "",
          dataUrl,
        });
      } catch {
        toast.error(`No se pudo procesar ${f.name}`);
      }
    }
    if (added.length) setExtraImages((prev) => [...prev, ...added]);
  };

  const inRange = (d) => {
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };
  const visible = tests.filter((t) => inRange(t.date));
  const toggle = (id) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setSelected(ns);
  };
  const toggleAll = () => {
    if (selected.size === visible.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((t) => t.id)));
    }
  };

  const generate = async () => {
    const ids = visible.filter((t) => selected.has(t.id)).map((t) => t.id);
    if (ids.length === 0) {
      toast.error("Selecciona al menos una prueba");
      return;
    }
    setGenerating(true);
    try {
      const resp = await api.post(
        `/patients/${patient.id}/clinical-report`,
        {
          test_ids: ids,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          conclusions,
          recommendations,
          audiologist_id: audiologistId || null,
          extra_images: extraImages.map((i) => ({
            name: i.name,
            caption: i.caption || "",
            data_base64: (i.dataUrl.split(",")[1] || ""),
          })),
        },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe-${patient.surname?.toLowerCase() || "paciente"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Informe descargado");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="clinical-report-dialog"
      >
        <DialogHeader>
          <DialogTitle>Generar informe clínico</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
            </div>
            <div>
              <Label>Audioprotesista firmante</Label>
              <Select value={audiologistId} onValueChange={setAudiologistId}>
                <SelectTrigger data-testid="report-audiologist"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  {(audiologists || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                      {a.role !== "audioprotesista" ? ` (${a.role})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border border-[#E5E3DB] rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E3DB] bg-[#F7F6F3]">
              <div className="text-sm font-medium">
                Pruebas a incluir ({selected.size} de {visible.length} seleccionadas)
              </div>
              <Button size="sm" variant="ghost" onClick={toggleAll} data-testid="report-toggle-all">
                {selected.size === visible.length ? "Deseleccionar todas" : "Seleccionar todas"}
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-[#E5E3DB]">
              {visible.length === 0 && (
                <div className="text-center text-sm text-[#6E7772] py-6">
                  No hay pruebas en el rango de fechas seleccionado.
                </div>
              )}
              {visible.map((t) => (
                <label
                  key={t.id}
                  className="flex items-start gap-2 px-3 py-2 hover:bg-[#F7F6F3] cursor-pointer"
                  data-testid={`report-test-${t.id}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">
                      {TEST_TYPE_LABEL[t.test_type] || t.test_type}
                      <span className="ml-2 text-xs text-[#6E7772]">{t.date}</span>
                    </div>
                    {t.notes && <div className="text-xs text-[#6E7772] line-clamp-1">{t.notes}</div>}
                  </div>
                  {t.source === "noah" && (
                    <Badge className="bg-[#D1E0D7] text-[#2A2F2C] text-[10px]">Noah</Badge>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="border border-[#E5E3DB] rounded-lg">
            <div className="px-3 py-2 border-b border-[#E5E3DB] bg-[#F7F6F3] text-sm font-medium">
              Imágenes adicionales (otoscopia, fotos del conducto…)
            </div>
            <div className="p-3 space-y-3">
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  addImages(e.target.files);
                  e.target.value = "";
                }}
                data-testid="report-extra-images-input"
              />
              {extraImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {extraImages.map((img, i) => (
                    <div
                      key={img.id}
                      className="border border-[#E5E3DB] rounded-md p-2 space-y-1"
                      data-testid={`report-extra-image-${i}`}
                    >
                      <img src={img.dataUrl} alt={img.name} className="w-full h-28 object-cover rounded" />
                      <Input
                        placeholder="Pie de foto (opcional)"
                        value={img.caption}
                        onChange={(e) =>
                          setExtraImages((prev) =>
                            prev.map((x) => (x.id === img.id ? { ...x, caption: e.target.value } : x)),
                          )
                        }
                        className="h-8 text-xs"
                        data-testid={`report-extra-caption-${i}`}
                      />
                      <button
                        type="button"
                        className="text-xs text-[#C07F67] underline"
                        onClick={() => setExtraImages((prev) => prev.filter((x) => x.id !== img.id))}
                        data-testid={`report-extra-remove-${i}`}
                      >
                        Quitar
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#6E7772]">
                Se incluirán en el informe en una sección «Imágenes adjuntas» con su pie de foto.
              </p>
            </div>
          </div>

          <div>
            <Label>Conclusiones</Label>
            <Textarea
              rows={4}
              value={conclusions}
              onChange={(e) => setConclusions(e.target.value)}
              placeholder="Diagnóstico, hallazgos relevantes…"
              data-testid="report-conclusions"
            />
          </div>
          <div>
            <Label>Recomendaciones</Label>
            <Textarea
              rows={4}
              value={recommendations}
              onChange={(e) => setRecommendations(e.target.value)}
              placeholder="Recomendaciones de tratamiento, plan de adaptación, próxima revisión…"
              data-testid="report-recommendations"
            />
          </div>
        </div>

        <DialogFooter className="border-t border-[#E5E3DB] pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={generate}
            disabled={generating || selected.size === 0}
            className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            data-testid="report-generate"
          >
            <Download className="h-4 w-4 mr-1" />
            {generating ? "Generando…" : "Descargar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompareAudiogramsDialog({ audiograms, onClose }) {
  const COMPARE_COLORS = ["#4A5D4E", "#C07F67", "#1F4E79", "#7A5C92", "#C73E1D"];
  const sorted = [...audiograms].sort((a, b) => (a.date > b.date ? 1 : -1));
  // Por defecto: las 2 últimas
  const initial = sorted.slice(-2).map((t) => t.id);
  const [selected, setSelected] = useState(new Set(initial));

  const toggle = (id) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    if (ns.size > COMPARE_COLORS.length) {
      toast.error(`Máximo ${COMPARE_COLORS.length} audiogramas`);
      return;
    }
    setSelected(ns);
  };

  const chosen = sorted.filter((t) => selected.has(t.id));

  // Mostramos el primer seleccionado como base (con sus propios colores rojo/azul)
  // y el resto como overlays en colores grises/marcados.
  const [base, ...others] = chosen;
  const overlays = others.map((t, i) => ({
    series: t.parsed || {},
    color: COMPARE_COLORS[(i + 1) % COMPARE_COLORS.length],
    label: t.date,
  }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle>Comparar audiogramas</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 space-y-4">
          <p className="text-xs text-[#6E7772]">
            Selecciona 2 o más audiogramas para superponerlos. El primero (más antiguo seleccionado) se muestra con la simbología clínica habitual; los siguientes en colores distintos.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-[#E5E3DB] rounded-lg p-2 bg-[#FAF9F6]">
            {sorted.length === 0 && (
              <div className="text-center text-[#6E7772] py-4 text-sm col-span-2">No hay audiogramas.</div>
            )}
            {sorted.map((t, idx) => {
              const isSel = selected.has(t.id);
              const color = isSel ? COMPARE_COLORS[Array.from(selected).indexOf(t.id) % COMPARE_COLORS.length] : null;
              return (
                <label
                  key={t.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer text-sm ${isSel ? "border-[#4A5D4E] bg-white" : "border-[#E5E3DB] bg-white"}`}
                  data-testid={`compare-item-${t.id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggle(t.id)}
                  />
                  {color && <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: color }} />}
                  <div className="flex-1">
                    <div className="font-medium">{t.date}</div>
                    <div className="text-[10px] text-[#6E7772]">{t.test_label || "Audiograma"} · {t.source === "noah" ? "Noah" : "Manual"}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {chosen.length >= 2 && base ? (
            <div className="border border-[#E5E3DB] rounded-lg bg-white p-2">
              <AudiogramSvg parsed={base.parsed || {}} overlays={overlays} height={300} />
              <div className="flex flex-wrap gap-3 justify-center text-[11px] text-[#6E7772] pt-2">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: COMPARE_COLORS[0] }} />
                  {base.date} (base)
                </span>
                {others.map((t, i) => (
                  <span key={t.id} className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: COMPARE_COLORS[(i + 1) % COMPARE_COLORS.length] }} />
                    {t.date}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-[#6E7772] py-8 text-sm border border-dashed border-[#E5E3DB] rounded-lg">
              Selecciona al menos 2 audiogramas para ver la comparación.
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-[#E5E3DB] pt-3">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// --------------------------------------------------------------------------- //
// Accesorios asignados (cargador, aparato TV, mando…) — mismas categorías que el catálogo
// --------------------------------------------------------------------------- //
const STOCK_TYPE_LABEL = {
  audifono: "Audífono",
  accesorio: "Accesorio",
  pila: "Pilas",
  molde: "Molde",
  tapon_bano_medida: "Tapón baño (a medida)",
  tapon_bano_standard: "Tapón baño (estándar)",
  tapon_ruido_medida: "Tapón ruido (a medida)",
  tapon_ruido_standard: "Tapón ruido (estándar)",
  reparacion: "Reparación",
  otro: "Otro",
};

function AccessoriesSection({ patientId, user }) {
  const [accessories, setAccessories] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    stock_item_id: "",
    serial_number: "",
    category: "accesorio",
    brand: "",
    model: "",
    price: "",
    assigned_date: new Date().toISOString().slice(0, 10),
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const isStockMgr = ["admin", "center_admin", "gerente"].includes(user?.role);
  const canWrite = isStockMgr || user?.role === "audioprotesista";

  const load = () =>
    api.get(`/patients/${patientId}/accessories`).then((r) => setAccessories(r.data || [])).catch(() => {});

  useEffect(() => {
    load();
    if (isStockMgr) {
      api.get("/stock")
        .then((r) => setStockItems((r.data || []).filter((s) => s.type !== "audifono" && (s.quantity || 0) > 0)))
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [patientId]);

  const pickStock = (sid) => {
    const s = stockItems.find((x) => x.id === sid);
    if (!s) return;
    setForm((f) => ({
      ...f,
      stock_item_id: sid,
      category: s.type || "accesorio",
      brand: s.brand || "",
      model: s.model || "",
      price: s.sale_price ?? s.unit_price ?? "",
      serial_number: "",
    }));
  };

  const selectedStock = stockItems.find((x) => x.id === form.stock_item_id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/accessories`, {
        ...form,
        stock_item_id: form.stock_item_id || null,
        price: parseFloat(form.price) || 0,
      });
      toast.success("Accesorio asignado al paciente");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeAcc = async (a) => {
    if (!window.confirm("¿Quitar este accesorio? Si venía del stock, la unidad volverá al stock.")) return;
    try {
      await api.delete(`/accessories/${a.id}`);
      toast.success("Accesorio retirado (devuelto al stock si procedía)");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Card className="border-[#E5E3DB] shadow-none" data-testid="accessories-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-heading">
            Otros dispositivos asignados (cargador, aparato TV, mando…)
          </CardTitle>
          {canWrite && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white" data-testid="add-accessory-btn">
                  <Plus className="h-4 w-4 mr-1" /> Asignar accesorio
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Asignar accesorio al paciente</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  {isStockMgr && stockItems.length > 0 && (
                    <div>
                      <Label>Elegir del stock (descuenta 1 unidad)</Label>
                      <Select value={form.stock_item_id || "none"} onValueChange={(v) => (v === "none" ? setForm({ ...form, stock_item_id: "" }) : pickStock(v))}>
                        <SelectTrigger data-testid="accessory-stock-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Entrada manual (sin stock) —</SelectItem>
                          {stockItems.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {(s.brand || "—") + " " + (s.model || "")} · {STOCK_TYPE_LABEL[s.type] || s.type} · {s.quantity} ud.
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selectedStock && (selectedStock.serials || []).length > 0 && (
                    <div>
                      <Label>Número de serie</Label>
                      <Select value={form.serial_number || "none"} onValueChange={(v) => setForm({ ...form, serial_number: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="accessory-serial-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin número de serie</SelectItem>
                          {selectedStock.serials.map((sn) => (
                            <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Categoría</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={!!form.stock_item_id}>
                        <SelectTrigger data-testid="accessory-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STOCK_TYPE_LABEL).filter(([k]) => k !== "audifono").map(([k, lbl]) => (
                            <SelectItem key={k} value={k}>{lbl}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fecha de asignación</Label>
                      <Input type="date" value={form.assigned_date} onChange={(e) => setForm({ ...form, assigned_date: e.target.value })} />
                    </div>
                    <div>
                      <Label>Marca</Label>
                      <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} disabled={!!form.stock_item_id} data-testid="accessory-brand-input" />
                    </div>
                    <div>
                      <Label>Modelo</Label>
                      <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} disabled={!!form.stock_item_id} data-testid="accessory-model-input" />
                    </div>
                    {!selectedStock?.serials?.length && (
                      <div>
                        <Label>Nº de serie (opcional)</Label>
                        <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
                      </div>
                    )}
                    <div>
                      <Label>Precio venta (IVA incl.) €</Label>
                      <Input type="number" step="0.01" min={0} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="accessory-price-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Notas</Label>
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button type="submit" disabled={saving} className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white" data-testid="save-accessory-btn">
                      {saving ? "Guardando…" : "Asignar"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {accessories.length === 0 ? (
          <p className="text-sm text-[#6E7772]">Sin accesorios asignados. Aparecerán como primera opción al facturar, igual que los audífonos.</p>
        ) : (
          <div className="space-y-2">
            {accessories.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-[#E5E3DB] rounded-md px-3 py-2" data-testid={`accessory-row-${a.id}`}>
                <div className="flex items-center gap-3 text-sm">
                  <Badge className="bg-[#EAE6DF] text-[#2A2F2C]">{STOCK_TYPE_LABEL[a.category] || a.category || "Accesorio"}</Badge>
                  <span className="font-medium">{(a.brand || "—") + " " + (a.model || "")}</span>
                  {a.serial_number && <span className="text-xs text-[#6E7772] font-mono">S/N {a.serial_number}</span>}
                  {a.price > 0 && <span className="text-xs text-[#6E7772]">{Number(a.price).toFixed(2)} €</span>}
                  {a.assigned_date && <span className="text-xs text-[#6E7772]">{formatDate(a.assigned_date)}</span>}
                </div>
                {canWrite && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#9CA3AF] hover:text-[#C04848]" onClick={() => removeAcc(a)} data-testid={`remove-accessory-${a.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
